import React from "react";
import { DICTIONARIES, browserLocale, readStored, writeStored, translate } from "./i18n.jsx";
import { api } from "./api.jsx";
import { shrinkPhoto } from "./shrink-photo.js";
import { useRouter } from "./router.jsx";
import { track } from "./analytics.jsx";
import { currentState } from "./push.jsx";

const AppCtx = React.createContext(null);
export const useApp = () => React.useContext(AppCtx);

/**
 * A TRADE WAITING ON YOU: one to answer, or an accepted one you have still to
 * mark done — by the server's `can`. One predicate, because the shop's rail
 * badge, Today and the Requests tab of the same name must count the same
 * thing; the tab listed accepts alone, and a trade waiting for Mark done was
 * counted on the rail and nowhere to be done in Requests.
 */
export const waitsOnYou = (x) => !!(x.can && (x.can.accept || x.can.complete));

export function useMedia(query) {
  const [on, setOn] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mm = window.matchMedia(query); const h = () => setOn(mm.matches);
    mm.addEventListener('change', h); return () => mm.removeEventListener('change', h);
  }, [query]);
  return on;
}

let nextId = 100;
const uid = (p) => p + ++nextId;

/**
 * An abort that happened because we replaced the request, not because anything
 * broke.
 *
 * `code === 20` is the legacy DOMException number, kept because a browser that
 * does not set `name` on an aborted fetch still sets that.
 */
const aborted = (e) => Boolean(e) && (e.name === 'AbortError' || e.code === 20);

/**
 * One request in flight per loader, and only the newest answer is believed.
 *
 * BOTH HALVES, and they do different jobs. The abort tells the browser to stop
 * caring about the old request — it frees the connection and stops the server's
 * answer being parsed. The counter is what makes a reply that arrives ANYWAY
 * harmless, and one always can: a response already on the wire when `abort()` is
 * called still resolves in some browsers, and a request that finished a
 * millisecond before the abort resolves in all of them.
 *
 * Without the counter the failure is silent and looks like nothing at all.
 * `loadMine` fired twice in quick succession, with the first answer landing
 * second, overwrote the newer list with the older one — and the screen then
 * shows a perfectly normal, perfectly stale grid. Nothing throws, nothing is
 * empty, no state says "error". That is the whole reason this is here and not
 * only on the loaders whose races were noticed.
 *
 * ONE OF THESE PER LOADER, not one shared between them. They abort each other,
 * so a single instance would mean opening the inbox cancelled the trades.
 *
 * The returned object is stable across renders on purpose: every loader below is
 * a `useCallback` whose identity feeds an effect, and a helper that changed
 * identity every render would restart all of them on every render.
 */
function useLatestRun() {
  const run = React.useRef(0);
  const inFlight = React.useRef(null);
  return React.useMemo(() => ({
    begin() {
      const id = ++run.current;
      if (inFlight.current) inFlight.current.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      return { signal: controller.signal, latest: () => id === run.current };
    },
  }), []);
}

/**
 * WHICH ASKING OF A PAGED LIST THE ROWS ON SCREEN BELONG TO, so a "Show more"
 * cannot cross a change of sort or filter. One per paged list.
 *
 * `reload()` is called by the loader that asks for the FIRST page, and returns
 * the function it calls once that page is on screen, cursor and all. `page()`
 * is called by "Show more" before it asks: it answers null while a reload is
 * on its way — the cursor on screen is then the OLD filter's, and sent with
 * the new one the server resumes a list from a row that list does not hold —
 * and otherwise a check that the page, when it comes back, still belongs: if
 * a reload was asked for meanwhile it is the old filter's next page, and
 * added under the new one it puts rows on screen the new list had not reached
 * and replaces its cursor, so the rest of the list is out of reach.
 *
 * Found on the Stock table (a93e05a) and then in browse, the wants list and a
 * trader's shelf, which had the same shape. Separate from `useLatestRun`, which
 * says only which request OF ONE LOADER may write: the first page and "Show
 * more" are two loaders with two runs, and neither saw the other.
 */
export function usePagedList() {
  const asked = React.useRef(0);
  const shown = React.useRef(0);
  return React.useMemo(() => ({
    reload() {
      const n = ++asked.current;
      return () => { shown.current = n; };
    },
    page() {
      const n = asked.current;
      return shown.current === n ? () => asked.current === n : null;
    },
  }), []);
}

/**
 * Two hundred milliseconds of sine wave, and the two things that stop it working.
 *
 * ONE CONTEXT, REUSED. A browser allows only a handful of AudioContexts per page
 * — around six in Chrome — and the old code built a fresh one per chime and
 * closed it in `onended`. A context that never starts never ends, so it never
 * closed, and after a few silent attempts every later one failed to construct:
 * the sound stopped for good and nothing said why.
 *
 * RESUMED BEFORE USE. A context created while the tab is hidden starts
 * suspended. `start()` then schedules a note on a clock that is not running, so
 * nothing is heard and no error is raised — which is exactly the case this
 * feature exists for. Resuming is what makes it audible; it still fails when the
 * page has never been interacted with, and that block is correct.
 *
 * WebAudio rather than an audio file: no asset, no request, no decoding.
 */
let audio = null;

/**
 * The notes, and why these ones.
 *
 * A major sixth (A5 to F#6) then the octave above the root: rising, consonant,
 * and resolved — it sounds like an announcement rather than an alarm. The single
 * 880 Hz sine this replaces was a beep, which is the sound of something being
 * wrong, and it is the wrong thing to say about a message from somebody who
 * wants to trade.
 *
 * Overlapping rather than sequential. Struck 70 ms apart while each rings for
 * 340 ms, so the three sound as one chord arriving, the way a real chime does.
 * Played end to end they would be three beeps in a row, which is worse than one.
 */
const CHIME = [
  { hz: 880.00, at: 0.00 },   // A5
  { hz: 1479.98, at: 0.07 },  // F#6, a major sixth up
  { hz: 1760.00, at: 0.14 },  // A6
];

/**
 * A short chime, and the two things that stop it working.
 *
 * ONE CONTEXT, REUSED. A browser allows only a handful of AudioContexts per page
 * — around six in Chrome — and the old code built a fresh one per chime and
 * closed it in `onended`. A context that never starts never ends, so it never
 * closed, and after a few silent attempts every later one failed to construct:
 * the sound stopped for good and nothing said why.
 *
 * RESUMED BEFORE USE. A context created while the tab is hidden starts
 * suspended. `start()` then schedules notes on a clock that is not running, so
 * nothing is heard and no error is raised — which is exactly the case this
 * feature exists for. Resuming is what makes it audible; it still fails when the
 * page has never been interacted with, and that block is correct.
 *
 * WebAudio rather than an audio file: no asset, no request, no decoding, and the
 * notes are legible here rather than buried in a binary somebody has to open a
 * sound editor to change.
 */
async function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audio || audio.state === 'closed') audio = new Ctx();
    if (audio.state === 'suspended') await audio.resume();
    if (audio.state !== 'running') return;

    const now = audio.currentTime;
    for (const note of CHIME) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      // Triangle, not sine: a few quiet harmonics give it a struck quality
      // instead of a test tone, without the edge a square or sawtooth has.
      osc.type = 'triangle';
      osc.frequency.value = note.hz;

      const t = now + note.at;
      /*
       * Fast in, slow out — a struck envelope.
       *
       * Ramps rather than steps because a hard start or stop clicks, and
       * exponential rather than linear because loudness is perceived that way:
       * a linear fade sounds like it stops halfway.
       */
      /*
       * PEAK, AND THE HEADROOM IT LEAVES. Reported as too quiet at 0.09, which it
       * was — a chime for a message from a stranger has to carry across a room
       * with a phone face down on a table.
       *
       * 0.20 is a little over double the amplitude, roughly +7 dB. The three
       * notes overlap, so the ceiling matters: struck 70 ms apart and decaying
       * over 340, the worst moment is around the third strike with the first two
       * partly decayed — near 0.47 summed, comfortably under the 1.0 where
       * WebAudio starts clipping. Going much past this would distort before it
       * got louder, which sounds broken rather than loud.
       */
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.20, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);

      osc.connect(gain).connect(audio.destination);
      osc.start(t);
      osc.stop(t + 0.36);
      // Disconnected rather than closed: the context is reused, and a closed one
      // cannot be reopened.
      osc.onended = () => { try { osc.disconnect(); gain.disconnect(); } catch (e) { /* already gone */ } };
    }
  } catch (e) {
    // Blocked, unsupported, or the tab has no audio. None is worth saying.
  }
}

/*
 * WHICH SCREENS READ THE MARKET — browse's nearby listings, the expensive
 * distance-sorted scan. Browse draws it; a listing page draws "More from" out
 * of it; the wants panel on `you` finds the listing a want opens from it.
 * Nothing else reads it, and a shop acting as itself reads it nowhere: it never
 * sees browse, and browse never returns your own cards, so its own listing's
 * "More from" is empty whatever is loaded.
 *
 * Two callers, one gate. The load effect LOADS the market on every screen
 * this answers yes for. The poller REFRESHES it on browse only, where the
 * market is the screen, and asks this as well as `=== 'browse'`, so the
 * poller's set is always a subset of the load's and the two cannot drift.
 * This is the one place that says which screens may cost a market query at
 * all.
 */
const MARKET_SCREENS = new Set(['browse', 'listing', 'you']);
/* The orderings browse offers, as the server names them (SORTS in browse.jsx). */
const BROWSE_SORTS = ['closest', 'price_asc', 'price_desc', 'newest'];
const readsMarket = (routeName, trader) => MARKET_SCREENS.has(routeName) && !(trader && trader.kind === 'shop');

export function AppProvider({ children }) {
  /**
   * Where the app starts is decided by the server, not by a flag in localStorage.
   * A flag says "this browser has seen onboarding"; only the session cookie says
   * "this person has an account", and that is the question worth asking. Until the
   * answer arrives we render nothing rather than guessing and flashing the wrong
   * screen.
   */
  const [trader, setTrader] = React.useState(null);
  /* This account's own counter, when it is a shop. See `/api/me`. */
  const [myShop, setMyShop] = React.useState(null);
  /**
   * What the server requires of an account, answered by the server rather than
   * assumed here. Defaults to requiring confirmation so that a boot which has not
   * heard back yet errs the same way the server does.
   */
  const [serverConfig, setServerConfig] = React.useState({ requireEmailVerification: true });
  const [booting, setBooting] = React.useState(true);
  // The address bar is the source of truth for where we are — see router.jsx.
  const { route, navigate, back, cameFrom } = useRouter();
  /** What the guest was trying to do when they were asked to sign in. */
  const [intent, setIntent] = React.useState(null);

  /**
   * The language the interface is in.
   *
   * Order of authority: what the trader chose (remembered on their account and in
   * this browser), then what the browser asks for, then Spanish — which is the
   * fallback rather than English, because the product launches in Spain.
   */
  const [locale, setLocaleState] = React.useState(
    () => readStored() || browserLocale(),
  );
  const t = React.useCallback((key, vars) => translate(locale, key, vars), [locale]);
  // The API client formats dates, money and its own error messages, so it needs the
  // locale from the first render — not only from the moment somebody presses the switch.
  React.useEffect(() => { api.setLocale(locale); document.documentElement.lang = locale; }, [locale]);
  const [listings, setListings] = React.useState([]);
  const [listingsState, setListingsState] = React.useState('loading');   // loading | ready | error
  const [wants, setWants] = React.useState([]);

  /*
   * Where each list ended, and nothing more.
   *
   * A cursor per list rather than a page number: the server orders browse by
   * distance and the others by time, and "page 3" of a list that is still moving
   * is a position that means something different by the time it is asked for.
   * Null means there is no more; the "load more" control appears exactly when
   * one of these is set, so "nothing further" and "we forgot to say" cannot look
   * the same on screen.
   */
  /*
   * What is waiting, from the cheap endpoint.
   *
   * Held separately from `threads` and `trades` because it arrives without
   * them: a tab on Browse polls these two numbers and nothing else, so the
   * badge and the chime cannot be derived from lists that are not being
   * fetched.
   */
  /*
   * `null` until the first answer, and that is load-bearing rather than tidy.
   *
   * It used to start at { messages: 0, trades: 0 }, which is indistinguishable
   * from a real answer of zero. The chime records its baseline on the first
   * measurement and stays quiet for it — but the first measurement was those
   * placeholder zeros, taken before anything had been asked. The real numbers
   * then arrived as an INCREASE from nothing, so opening the site with two
   * unread messages sounded a chime for news that was hours old, which is the
   * exact case the comment on that effect says it prevents.
   */
  const [pulse, setPulse] = React.useState(null);

  const [cursors, setCursors] = React.useState({ listings: null, mine: null, wants: null });
  const [loadingMore, setLoadingMore] = React.useState(null);

  /*
   * Whether somebody has paged past the first page of a list.
   *
   * The poll refetches browse and your own listings every few seconds and
   * REPLACES them with page one — which is right while page one is all there
   * is, and wrong the moment somebody presses "show more": the new rows appeared
   * and then vanished a few seconds later, reported exactly that way.
   *
   * A ref, not state, because the polling effect must not restart every time
   * this changes — restarting it would reset the backoff and fire an immediate
   * request, turning a paging click into an extra round trip.
   */
  const paged = React.useRef({ listings: false, mine: false });

  /** When the market was last refreshed, so it can run on its own clock. */
  const lastMarket = React.useRef(0);

  const [trades, setTrades] = React.useState([]);
  const [threads, setThreads] = React.useState([]);
  /*
   * Whether these three lists have been ANSWERED yet, which is not the same
   * question as whether they are empty.
   *
   * They had no such state at all, so every screen reading them treated "not
   * arrived" as "there is nothing" — the inbox said "No messages yet", trades
   * said "No meetups agreed yet" and the wants list said "No wants yet", each
   * over a request that was still in flight, and each with a button inviting
   * somebody to act on it. That is the same mistake `listing.jsx` documents at
   * length about `missing`: a claim about the world made before anybody asked.
   *
   * `loading` until a real answer, then `ready` or `error`. It goes back to
   * `loading` on a refetch, which costs nothing because every screen also
   * requires the list to be empty before it draws placeholders — a poll over a
   * list somebody is reading must not replace it with grey boxes.
   */
  const [tradesState, setTradesState] = React.useState('loading');   // loading | ready | error
  const [threadsState, setThreadsState] = React.useState('loading');
  const [wantsState, setWantsState] = React.useState('loading');
  /*
   * HOW MANY THERE ARE, kept apart from how many are loaded.
   *
   * The Selling tab counted the array and told somebody who had imported five
   * thousand listings that they had fifty — the size of one page. The wants tab
   * was about to get the same count for the same reason. The server sends a
   * total on the first page now; these hold it, so a scroll cannot change it.
   *
   * Null until the first answer, so a tab shows no count rather than a zero
   * that is really "not asked yet".
   */
  const [wantsTotal, setWantsTotal] = React.useState(null);
  const [myCardsTotal, setMyCardsTotal] = React.useState(null);
  /** Messages of the conversation currently open, keyed by its id. */
  const [openThread, setOpenThread] = React.useState({ id: null, messages: [], loading: false });
  /*
   * A ref beside the state, read by the poller.
   *
   * The polling effect must not depend on the open conversation, or every message
   * would tear the interval down and start a new one — the timer would keep
   * resetting and, in a busy thread, might never fire.
   */
  const openThreadRef = React.useRef(openThread);
  React.useEffect(() => { openThreadRef.current = openThread; }, [openThread]);

  /*
   * Which screen is showing, for the poller.
   *
   * A ref rather than a dependency: the polling effect must not restart on every
   * navigation, because restarting resets the backoff and fires an immediate
   * request — so moving between screens would become a burst of them.
   */
  const routeRef = React.useRef(route.name);
  React.useEffect(() => {
    routeRef.current = route.name;
    /*
     * Leaving the inbox closes the thread.
     *
     * Otherwise it stays "open" forever, and the next visit to any screen keeps
     * a conversation nobody is looking at permanently marked as read.
     */
    if (route.name !== 'inbox') setOpenThread((prev) => (prev.id === null ? prev : { id: null, messages: [], loading: false }));
  }, [route.name]);
  /** Who is signed in, for the poller's market gate — a ref for the reason above. */
  const traderRef = React.useRef(trader);
  React.useEffect(() => { traderRef.current = trader; }, [trader]);
  /** Someone we are about to message for the first time. See messageTrader. */
  const [pending, setPending] = React.useState(null);
  const clearPending = React.useCallback(() => setPending(null), []);
  const [radius, setRadius] = React.useState('5 km');
  // [lat, lng], or null until the trader sets one — from their device or a map pin.
  // There is no place name anywhere in this product: see the design system README.
  /**
   * Where the viewer is.
   *
   * Read back from this device at boot, because a guest has no account to hold it
   * and losing it on refresh made the product useless to anybody who had not
   * signed up yet — they set a location, reloaded, and were shown every listing in
   * Spain again.
   *
   * It is a coordinate the person chose, kept on their own machine, and sent only
   * to our own API to answer "how far is this from me". It is not an identifier
   * and it is not shared with anyone. A signed-in trader's account is still the
   * authority; this is what fills the gap before there is one.
   */
  const [point, setPointState] = React.useState(() => {
    try {
      const raw = window.localStorage.getItem('cbm.point');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length === 2 && parsed.every(Number.isFinite) ? parsed : null;
    } catch (e) {
      return null;
    }
  });
  /**
   * The places this trader has saved, newest last, exactly as the server ordered
   * them.
   *
   * Server-owned and never patched locally. Every mutation answers with the whole
   * list because the server applies rules the client does not know — the cap, and
   * the promotion that happens when the active location is deleted (migration
   * 052). Reimplementing either here would be a second copy free to disagree with
   * the first, on the one screen where a disagreement is invisible: the chip would
   * name one place while every distance was measured from another.
   *
   * Empty for a guest, who has a point on their own device and no account to hang
   * a list on.
   */
  const [locations, setLocations] = React.useState([]);
  const [games, setGames] = React.useState([]);
  /*
   * The card languages you prefer to see. Migration 051.
   *
   * Beside `games` and behaving exactly like it, because it answers the same
   * question on a different axis. Empty is a real answer meaning "no preference",
   * which is why nothing anywhere fills it in on somebody's behalf.
   */
  const [languages, setLanguages] = React.useState([]);
  const [toast, setToast] = React.useState(null);
  /**
   * Interface preferences, kept on this device.
   *
   * `sound` is here rather than on the account on purpose: whether a beep is
   * welcome depends on the room you are in, not on who you are. The laptop at work
   * and the phone at home deserve different answers, and syncing it would get one
   * of them wrong.
   */
  const [settings, setSettingsState] = React.useState(() => {
    /*
     * `alerts` decides whether Browse tells you a want has turned up inside your
     * radius. It is stored per device for the same reason `sound` is.
     *
     * It had TWO controls and no reader: a checkbox on Browse under a heading
     * that said "Filters", and a switch in settings promising "one notification
     * when a want appears inside your radius". No such notification has ever been
     * sent by any channel — there is no push, no email, and nothing in the store
     * that watches for it. Both controls moved a value nothing consulted.
     *
     * The banner on Browse is the only want alert this product actually has, so
     * that is what this now switches, and the copy on both controls was rewritten
     * to describe it rather than a feature that does not exist.
     */
    const defaults = { alerts: true, sound: true };
    try {
      return { ...defaults, ...JSON.parse(window.localStorage.getItem('cbm.settings') || '{}') };
    } catch (e) {
      return defaults;
    }
  });
  const setSettings = React.useCallback((next) => {
    setSettingsState(next);
    try {
      window.localStorage.setItem('cbm.settings', JSON.stringify(next));
    } catch (e) {
      // A browser refusing storage is not a reason to refuse the preference.
    }
  }, []);
  const profile = trader
    ? {
      name: trader.displayName,
      handle: trader.handle ? '@' + trader.handle : '',
      trades: 0,
      rating: null,
      /*
       * WHAT WE MAY TELL THEM ABOUT, on both channels.
       *
       * From the account rather than this device: an email is sent by a server
       * to somebody who is not looking at any browser.
       *
       * ALL TEN, and the push three were MISSING — a real bug, found while
       * adding the draw pair. The server has always returned `pushWantMatch`
       * and this mapper has always dropped it, so `profile.pushWantMatch` was
       * undefined and the switch rendered from `!== false`, which is ON. Turn a
       * device switch off, reload, and it looked on again while the server kept
       * it off. The switch was wrong in both directions and nothing said so.
       *
       * This mapper enumerates, which is deliberate — a whole trader object
       * spread into `profile` would publish every field a screen could then
       * read by accident. The cost is exactly this: a field added on the server
       * reaches nothing until it is named here.
       */
      notifyWantMatch: trader.notifyWantMatch,
      notifyMessages: trader.notifyMessages,
      notifyTrades: trader.notifyTrades,
      pushWantMatch: trader.pushWantMatch,
      pushMessages: trader.pushMessages,
      pushTrades: trader.pushTrades,
      notifyDrawResult: trader.notifyDrawResult,
      pushDrawResult: trader.pushDrawResult,
      notifyNewCampaign: trader.notifyNewCampaign,
      pushNewCampaign: trader.pushNewCampaign,
      emailVerified: trader.emailVerified,
      /*
       * How this trader can be reached outside the app, and whether they have
       * agreed to show it. Both halves are needed here: the form shows what is
       * saved, and the switch shows whether it is published — and they are
       * deliberately not the same thing.
       */
      whatsapp: trader.whatsapp,
      whatsappVisible: trader.whatsappVisible,
      telegram: trader.telegram,
      telegramVisible: trader.telegramVisible,
      /*
       * No `visible` twin: there is no second consent to record. See the note
       * on the column — a field the mapper forgets is a field the screen never
       * sees, however complete the server's answer was.
       */
      cardmarketUsername: trader.cardmarketUsername,
    }
    : { name: 'You', handle: '', trades: 0, rating: null };

  const notify = React.useCallback((message, tone = 'success') => setToast({ message, tone, at: Date.now() }), []);

  /**
   * Screen views, recorded from the one place that already knows about every
   * navigation. Instrumenting each screen instead would mean remembering to do it
   * in every new one — and the one that gets forgotten is always the interesting one.
   */
  React.useEffect(() => {
    track('view', { path: window.location.pathname });
  }, [route.name, route.id]);

  // Ask the server who we are, once, before rendering anything.
  React.useEffect(() => {
    let live = true;
    api.me()
      .then((r) => {
        if (!live) return;
        setTrader(r.trader);
        /*
         * THE COUNTER THIS ACCOUNT KEEPS, when this account IS a shop.
         *
         * Sent by `/api/me` only for a shop, and held here because the screens
         * that need it are the two meetup dialogs — a trade involving a shop
         * happens at that shop's counter, and when the shop is the one doing
         * the proposing, nothing in the listing says so.
         */
        setMyShop(r.shop || null);
        // What this deployment expects, not what the app assumes. See /api/me.
        if (r.config) setServerConfig(r.config);
      })
      // 401 is not an error here — it is the answer "nobody is signed in", and a
      // guest is a first-class visitor. Browsing is what convinces someone the
      // product is worth an account; demanding the account first asks them to
      // trust it before they have seen anything.
      .catch(() => undefined)
      .finally(() => { if (live) setBooting(false); });
    return () => { live = false; };
  }, []);

  // The API's error messages must be in the same language as the screen around
  // them, or a form says one thing in Spanish and the error under it another.
  React.useEffect(() => { api.setLocale(locale); }, [locale]);

  /**
   * A signed-in trader's stored choice wins over this browser's.
   *
   * They set it deliberately, and it should follow them to a new device — which
   * is the whole reason it lives on the account and not only in localStorage.
   */
  React.useEffect(() => {
    if (trader && trader.locale && trader.locale !== locale && !readStored()) {
      setLocaleState(trader.locale);
    }
  }, [trader && trader.locale]);

  const setLocale = React.useCallback((next) => {
    if (!DICTIONARIES[next]) return;
    setLocaleState(next);
    writeStored(next);
    api.setLocale(next);
    // Persist it to the account too, so it follows them to another device. A
    // failure here is not worth telling anybody about: the language has already
    // changed on screen, which is what they asked for.
    if (trader) api.updateMe({ locale: next }).then((r) => setTrader(r.trader)).catch(() => undefined);
  }, [trader]);

  /**
   * Adopt the account's language on a device that has no opinion yet.
   *
   * Only when nothing is stored here: a choice made in THIS browser is the more
   * recent deliberate act, and having a second device silently overrule it is
   * worse than the language differing between two devices somebody uses.
   */
  React.useEffect(() => {
    if (!trader || !trader.locale) return;
    if (readStored()) return;
    if (!DICTIONARIES[trader.locale]) return;
    setLocaleState(trader.locale);
  }, [trader && trader.id]);

  /** The trader's own settings live on the server; mirror them locally when they load. */
  React.useEffect(() => {
    if (!trader) return;
    setRadius(`${Math.round(trader.radiusM / 1000)} km`);
    /*
     * The account's point wins, so the map follows you to another device.
     *
     * It arrives coarsened to about a hundred metres, which is all a pin needs;
     * the exact value stays on the server where the distances are computed. This
     * runs after this device's own stored point, deliberately: a signed-in trader
     * should see what their ACCOUNT says, since that is what the server filters
     * and measures against.
     */
    /*
     * THE SAME POINT IS NOT A CHANGE, and saying so costs a browse request.
     *
     * This built a fresh `[lat, lng]` every time. React compares by reference,
     * so an array holding the identical two numbers this device already had —
     * the usual case, since the point is also in localStorage — still counted
     * as new state. That re-rendered, which gave `loadListings` a new identity,
     * which re-ran the effect that calls it: a second listings request,
     * identical to the first, both of which the server does the work for.
     *
     * Returning the previous array unchanged makes React bail out of the render
     * entirely, which is the documented way to say "nothing happened".
     */
    if (trader.homePoint) {
      const { lat, lng } = trader.homePoint;
      setPointState((prev) => (prev && prev[0] === lat && prev[1] === lng ? prev : [lat, lng]));
    }
    if (trader.games && trader.games.length) setGames(trader.games);
    if (trader.languages && trader.languages.length) setLanguages(trader.languages);
  }, [trader && trader.id]);

  /**
   * The saved locations, read once per account.
   *
   * A separate request rather than a field on the session, and that is the whole
   * design decision: `resolveSession` runs on EVERY authenticated request in the
   * product, so a join added there is a join added to all of them. The list is
   * needed by two controls on one screen and changes when somebody edits it, which
   * is not a reason to make every poll carry it.
   *
   * A failure is silent. The chip falls back to showing the radius alone, which is
   * exactly what it showed before any of this existed — a location list that could
   * not be fetched is not a reason to interrupt somebody who was browsing.
   */
  /*
   * WHETHER WE YET KNOW WHERE THEY ARE — not whether they have a location.
   *
   * REPORTED as browse taking seconds with cancelled requests before it. Two
   * listing requests went out eleven milliseconds apart: the first with the
   * default radius and no point at all, the second with the trader's real
   * location. The first was superseded before it could ever have been useful,
   * and the server had already started work on it.
   *
   * It is not the abort machinery failing — that worked, and the screen was
   * never wrong. It is that the question was asked before the inputs to it had
   * arrived.
   *
   * `true` for a guest immediately, because there is nothing to wait for and
   * making somebody signed out wait on a request that will not happen would
   * trade one delay for another.
   */
  /*
   * WHOSE locations we have, rather than a boolean saying we have some.
   *
   * A flag does not work here, and the first attempt at this made browse worse
   * — three requests instead of two. Effects in one commit all close over the
   * values of THAT render: the locations effect setting `ready = false` does
   * not change what the load effect sees a moment later in the same commit, so
   * the load still fired on a stale `true` left over from the guest branch.
   *
   * Holding the id instead makes readiness DERIVED — computed in the same
   * render from the same two values, with no window in which one has moved and
   * the other has not. `null` is the settled answer for a guest; `undefined` is
   * "not yet asked", which is a different thing and has to be.
   */
  const [locationsForTrader, setLocationsForTrader] = React.useState(undefined);
  React.useEffect(() => {
    if (!trader) { setLocations([]); setLocationsForTrader(null); return; }
    let live = true;
    api.locations()
      .then((r) => { if (live) setLocations(r.locations || []); })
      .catch(() => undefined)
      /*
       * `finally`, so a location list that cannot be fetched still releases
       * browse. The alternative is a screen that never loads because of the one
       * request whose own comment says its failure is not worth interrupting
       * anybody over.
       */
      .finally(() => { if (live) setLocationsForTrader(trader.id); });
    return () => { live = false; };
  }, [trader && trader.id]);

  /*
   * True when we know where this viewer is, or that there is nobody to know
   * about. Derived, so it cannot disagree with `trader` for a render.
   */
  const locationsReady = trader ? locationsForTrader === trader.id : locationsForTrader === null;
  React.useEffect(() => { if (!toast) return; const timer = setTimeout(() => setToast(null), 3600); return () => clearTimeout(timer); }, [toast]);

  /**
   * Reloads the listings inside the current radius.
   *
   * The point goes with the request. For a signed-in trader the server already
   * holds one and prefers it; for a guest this is the only way it can answer the
   * question the whole product is about.
   */
  /*
   * Only the newest request may write each list. See `useLatestRun` above for
   * why both an abort and a counter are needed, and what a lost race looks like.
   *
   * Browse is the one where it was noticed: boot fires it more than once — the
   * effect runs as soon as booting ends, then again when the account's own point
   * and radius arrive from /api/me — and there was nothing stopping the first
   * answer from landing after the second. When it did, an older reply overwrote a
   * newer one, and the visible symptom was "sometimes a refresh shows no
   * listings, and refreshing again fixes it".
   *
   * The other six never had it, and were not noticed for the reason above: an
   * overwrite with stale data looks exactly like the screen working.
   */
  const listingsRun = useLatestRun();
  const mineRun = useLatestRun();
  const tradesRun = useLatestRun();
  const conversationsRun = useLatestRun();
  const wantsRun = useLatestRun();
  const pulseRun = useLatestRun();
  const referralRun = useLatestRun();
  const campaignsRun = useLatestRun();
  const moreRun = useLatestRun();
  /* So a "Show more" cannot cross a change of filter. See `usePagedList`; the stock's is `minePages`. */
  const listingsPages = usePagedList();
  const wantsPages = usePagedList();
  /*
   * BROWSE'S FILTERS, RESOLVED ONCE — and every one of them goes to the server.
   *
   * They used to be applied here, in the client, over the page that had already
   * arrived. Filtering to Japanese therefore showed the Japanese cards among the
   * fifty loaded, so a rare language read as "almost nothing here" when the
   * truth was "almost nothing in the first page"; and the counts under the tabs
   * were cut from the same fifty, which made a claim about the whole market out
   * of a sample nobody could see. Only `withPhotos` was ever asked of the
   * server, because it is the only one the client had no way to answer.
   *
   * The cursor is why they cannot be split between the two sides: a keyset is a
   * position within the population the query defines, so a filter applied after
   * the page arrived pages a different set than it returns, and page two resumes
   * from a row page one never contained. That is not a rendering bug — the
   * reader simply never sees some of the cards.
   *
   * RESOLVED IN ONE PLACE, and Browse reads them back from here rather than
   * working them out again. Two sources deciding what is being shown is how the
   * count under a tab comes to describe a different list than the grid.
   *
   * The URL wins over the saved preference whenever it says anything. A search
   * someone found useful is exactly the thing they send to a friend, and the
   * recipient's saved games are not the sender's — so a shared link has to show
   * what the sender saw. An empty selection is the absence of a filter, which is
   * also what an account that has never answered holds.
   */
  /*
   * PRESENCE, not truthiness.
   *
   * `route.game ? ... : games` could not tell "all games" from "no opinion",
   * because both arrive falsy — and it resolved both to the saved list. So
   * pressing "All games" put the chips straight back, and the only reason
   * languages looked like it worked is that most accounts have none saved.
   *
   * An empty list here means ALL, and it is the answer the URL gave: `?game=`
   * parses to `[]`, an absent key parses to nothing at all. See router.jsx.
   */
  const activeGames = route.game !== undefined && route.game !== null ? [].concat(route.game).filter(Boolean) : games;
  const activeLanguages = route.language !== undefined && route.language !== null ? [].concat(route.language).filter(Boolean) : languages;
  const browseMode = route.mode === 'sale' || route.mode === 'trade' ? route.mode : undefined;
  const onlyWants = route.only === 'wants';
  const withPhotos = route.photos === '1';
  const foil = route.foil === 'only' || route.foil === 'none' ? route.foil : undefined;
  /*
   * The search term, one beat behind the URL.
   *
   * The field writes to the URL on every keystroke and reads back from it, so
   * typing stays instant; this is the value that reaches the network. Without
   * the delay, "charizard" is nine browse queries — each one a distance-sorted
   * scan of the market — of which eight are thrown away by the request that
   * follows them. The filtering used to be local, so the cost of a keystroke was
   * nothing; it is not nothing any more.
   */
  // A picked card's English name where the link carries one (`card`, see
  // browse.jsx's `pickCard`); the box's text otherwise.
  const typed = route.card || route.q || '';
  const [searchTerm, setSearchTerm] = React.useState(typed);
  React.useEffect(() => {
    if (typed === searchTerm) return undefined;
    const timer = setTimeout(() => setSearchTerm(typed), 250);
    return () => clearTimeout(timer);
  }, [typed, searchTerm]);
  /*
   * The ordering, which the server now applies.
   *
   * Held here rather than on the screen because it is part of the REQUEST: the
   * cursor pages on whatever the ordering is, so changing the sort has to re-ask
   * from the first page exactly as changing a filter does. Sorting the arrived
   * page put the cheapest of the fifty loaded at the top and called it the
   * cheapest within the radius.
   *
   * A KEY, never the translated label. Holding "Más cerca" would mean a request
   * carrying a Spanish sentence the API has never heard of, and switching
   * language mid-session would silently stop the sort working.
   */
  /*
   * AND IN THE LINK (owner, 2026-10-22: view state lives in the URL, so a
   * reload or a shared link restores it). Browse writes `sort` beside its
   * filters; arriving on one carrying it, that is the first request's order,
   * and a link's order beats whatever this tab had. Absent leaves the store's
   * own alone, so a trip to a listing and back keeps it, as it always did.
   * Closest is the default and travels as no key. Anything else is ignored.
   */
  const linkedSort = route.name === 'browse' && BROWSE_SORTS.includes(route.sort) ? route.sort : null;
  const [heldSort, setSort] = React.useState(linkedSort || 'closest');
  /*
   * The link's order in THIS render, not one later: arriving in the app on a
   * link whose order differs from the held one would otherwise ask once in
   * the old order and again in the new. Held as well, so a trip to a listing
   * afterwards keeps it.
   */
  const sort = linkedSort || heldSort;
  React.useEffect(() => { if (linkedSort) setSort(linkedSort); }, [linkedSort]);
  /** What each tab would show. Null from the server means "unchanged". */
  const [browseCounts, setBrowseCounts] = React.useState(null);
  /*
   * Every filter, as the URL states them.
   *
   * `qs` drops what is undefined and expands an array into a repeated key, so an
   * unfiltered browse is the same request it has always been.
   *
   * This is what the CHIPS say. What the request carries is `browseQuery` below,
   * which is this one beat behind on a burst.
   */
  const requestedFilters = React.useMemo(() => ({
    game: activeGames.length ? activeGames : undefined,
    language: activeLanguages.length ? activeLanguages : undefined,
    mode: browseMode,
    q: searchTerm || undefined,
    onlyWants: onlyWants || undefined,
    withPhotos: withPhotos || undefined,
    foil,
    sort,
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the arrays are rebuilt
  // every render; their CONTENTS are the dependency, which is what the join pins.
  }), [activeGames.join(','), activeLanguages.join(','), browseMode, searchTerm, onlyWants, withPhotos, foil, sort]);

  /*
   * The filters the request carries: the first change immediately, a burst once.
   *
   * Every chip on browse — game, language, mode, sort, wants, photos — writes to
   * the URL, and the URL is a dependency of the browse request. Picking four
   * filters in a couple of seconds therefore asked the server four times, and
   * three of those answers were thrown away by the one that followed them. The
   * client was never confused by it — `loadListings` aborts the previous request
   * and discards a late reply — but the server had already done the work, and
   * that work is a distance-sorted scan of every active listing in radius.
   *
   * LEADING EDGE, which is the whole design and the opposite of the search box
   * below it. A chip is a deliberate click and its answer is expected now, so the
   * first one fires on the spot; anything within 250ms of it is folded into a
   * single follow-up carrying the final state. Four quick clicks are two
   * requests, and one considered click is still one request with nothing added
   * to it. A plain trailing debounce would have put 250ms in front of EVERY
   * filter change, which is the common case — the burst is the rare one.
   *
   * `q` keeps its own trailing debounce (`searchTerm`, above) and reaches here
   * already delayed: typing genuinely wants the last value rather than the first,
   * because the intermediate ones are prefixes nobody asked to search for.
   */
  /*
   * How long a "burst" is. The same 250ms as the search box by coincidence
   * rather than by sharing: that one is how long a typist pauses between
   * keystrokes, this one is how close two clicks have to be to count as one
   * decision. They are free to move apart.
   */
  const FILTER_BURST_MS = 250;
  const [browseQuery, setBrowseQuery] = React.useState(requestedFilters);
  const filtersAppliedAt = React.useRef(0);
  React.useEffect(() => {
    if (requestedFilters === browseQuery) return undefined;
    const apply = () => {
      filtersAppliedAt.current = Date.now();
      setBrowseQuery(requestedFilters);
    };
    /*
     * Nothing has been applied yet on a first render, so `since` is enormous and
     * the first filter change of a session is immediate — which is right: it is
     * a click, not the tail of a burst.
     */
    const since = Date.now() - filtersAppliedAt.current;
    if (since >= FILTER_BURST_MS) { apply(); return undefined; }
    /*
     * The wait is what is LEFT of the window, not a fresh one, so a burst
     * settles 250ms after it started rather than 250ms after it stopped. The
     * cleanup drops the pending follow-up on every further click, so a burst of
     * any length still costs exactly one.
     */
    const timer = setTimeout(apply, FILTER_BURST_MS - since);
    return () => clearTimeout(timer);
  }, [requestedFilters, browseQuery]);

  const loadListings = React.useCallback(() => {
    /*
     * THE MARKET WAS JUST LOADED, said here so the poll believes it.
     *
     * `lastMarket` began at 0, so the first tick of the poll always found the
     * market thirty seconds overdue and fetched it again — a second identical
     * request on every cold load, moments after the one the mount had already
     * made. Reported as browse being slow with cancelled requests, and it was
     * the half that had nothing to do with the database.
     *
     * Recorded from inside the loader rather than at its call sites, because
     * there are several and a marker that four of them remember is a bug in the
     * fifth.
     *
     * WHAT THIS CHANGES, since the poll's own comment relied on the opposite:
     * returning to browse within thirty seconds no longer refetches. That was
     * deliberate — "the first tick on arrival is already due" — and it is
     * better this way. Data half a minute old is what the poll considers fresh
     * anywhere else, and a refetch on every tab switch is the thing this loop
     * was rewritten to stop doing.
     */
    lastMarket.current = Date.now();
    const run = listingsRun.begin();
    const shown = listingsPages.reload();
    setListingsState('loading');
    const origin = point ? { lat: point[0], lng: point[1] } : {};
    return api.listings({
      limit: 60, radiusM: Math.round(parseFloat(radius) * 1000), ...origin, ...browseQuery,
    }, run.signal)
      .then((r) => {
        if (!run.latest()) return;
        setListings(r.listings.map(api.toListingView));
        setCursors((c) => ({ ...c, listings: r.nextCursor ?? null }));
        shown();
        /*
         * What each tab would show, with every OTHER filter still applied.
         *
         * Straight from the server, because it is the only side that can count
         * past the page it sent. The tabs used to count the fetched listings,
         * which meant "For sale 12" was a claim about the market made from
         * whatever sixty rows had arrived — and it was believed, because a
         * number reads as an answer in a way a grid does not.
         */
        setBrowseCounts(r.counts ?? null);
        // Back to one page, so the poll may refresh it again.
        paged.current.listings = false;
        setListingsState('ready');
      })
      .catch((e) => {
        // An abort is this function replacing itself, not a failure worth a toast.
        if (!run.latest() || aborted(e)) return;
        setListingsState('error');
        notify(e.message, 'caution');
      });
  }, [notify, point, radius, browseQuery]);

  /**
   * Your own listings, fetched separately.
   *
   * They used to be filtered out of `listings`, which only worked while browse
   * returned them — and browse no longer does, because you cannot go and collect
   * your own card. Asking for them explicitly is also the only way the Selling tab
   * can show a listing that is outside your own radius, which yours can be.
   */
  const [myCards, setMyCards] = React.useState([]);
  const [myCardsState, setMyCardsState] = React.useState('loading');   // loading | ready | error
  /*
   * HOW THE SHELF IS NARROWED, held here rather than on the screen.
   *
   * The shop's back office carries a row of game chips and a search box across
   * its sections, and the page after the first has to be fetched with the SAME
   * narrowing — a cursor is a position in a filtered list, so resuming it with
   * a different filter resumes from a row that list does not contain. That is
   * the lesson browse already learned and wrote down in `loadMore`.
   */
  /*
   * `sort` too, the Stock table's ordering, for the same reason: the next page
   * pages on the ordering the first one came in, and the server is what orders
   * it — sorting the page already fetched would be the newest fifty rearranged.
   * 'newest' is the server's default and travels as no key at all.
   */
  const mineFilter = React.useRef({ games: [], q: '', stale: false, synced: false, sort: 'newest' });
  const [myCardsByGame, setMyCardsByGame] = React.useState(null);
  /* So a "Show more" cannot cross a change of sort or filter. See `usePagedList`. */
  const minePages = usePagedList();

  const loadMine = React.useCallback((next) => {
    /*
     * Signing out begins a run too, so it aborts whatever was in flight and
     * outranks it. Otherwise the previous account's listings could land after the
     * list had been cleared and quietly put them back on screen.
     */
    const run = mineRun.begin();
    const shown = minePages.reload();
    if (!trader) { setMyCards([]); setMyCardsState('ready'); shown(); return Promise.resolve(); }
    /*
     * MERGED, NOT REPLACED. The chips and the search box are two independent
     * controls over one list: typing a card name must not quietly drop the game
     * somebody had narrowed to, and picking a game must not wipe what they
     * typed. Each call says only the part it changed.
     */
    if (next) mineFilter.current = { ...mineFilter.current, ...next };
    const { games, q, stale, synced, sort } = mineFilter.current;
    setMyCardsState('loading');
    return api.myListings({
      ...(games.length ? { game: games } : {}),
      ...(q ? { q } : {}),
      ...(stale ? { stale: '1' } : {}),
      // The Stock table's "Synced" chip: only what the CardTrader sweep brought in.
      ...(synced ? { synced: '1' } : {}),
      ...(sort !== 'newest' ? { sort } : {}),
    }, run.signal)
      .then((r) => {
        if (!run.latest()) return;
        setMyCards(r.listings.map(api.toListingView));
        // Sent on the first page only; a later page leaves the number alone.
        if (r.total != null) setMyCardsTotal(r.total);
        /*
         * The per-game tally, which the chips read. Unfiltered by design — see
         * the route — so it keeps saying how many Pokémon cards there are while
         * the grid is showing the Magic ones.
         */
        if (r.byGame) setMyCardsByGame(r.byGame);
        setCursors((c) => ({ ...c, mine: r.nextCursor ?? null }));
        shown();
        paged.current.mine = false;
        setMyCardsState('ready');
      })
      // NOT a silent catch. "My listings do not appear" is undiagnosable when the
      // only thing that happened was a swallowed rejection: no message, no state,
      // nothing in the console. An empty list and a failed request look identical
      // to the person looking at them, and they are not the same thing.
      //
      // An abort is neither of those. It is this loader replacing itself, and
      // painting the error state for it would report a superseded request as an
      // outage on a screen that is about to be filled correctly.
      .catch((e) => { if (run.latest() && !aborted(e)) setMyCardsState('error'); });
  }, [trader]);
  /*
   * NOT FOR A SHOP. Its back office loads its own stock, scoped to the games
   * it is working (see `ShopShell`'s arrival effect), and the first request
   * on arrival has to be that scoped one — not this whole one, which the
   * scoped one superseded a tick later and which cost the server a page of
   * the counter nobody was going to see. A guest still goes through
   * `loadMine`, which is what settles the state for one.
   */
  React.useEffect(() => { if (!(trader && trader.kind === 'shop')) loadMine(); }, [loadMine]);

  /*
   * Held until we know where they are, so the first request is the right one
   * rather than the first of two. See `locationsReady`.
   *
   * AND ONLY WHILE A SCREEN THAT READS IT IS SHOWING — `readsMarket`.
   *
   * `loadListings` is rebuilt whenever browse's filters change, and one of
   * them is the address's `game`, which the shop's back office writes on every
   * chip tap (shared on purpose: a link reads in the product's one
   * vocabulary). Ungated, this effect answered every tap — "All games" too —
   * with the distance-sorted scan the poller calls the bulk of all database
   * time, for a screen that draws none of it. The poller was gated by what is
   * on screen; this, its other caller, was not.
   *
   * Not browse alone, which is the poller's own test: a listing opened cold
   * draws "More from" out of the market and lost it under that gate — tried,
   * and the language spec's cold-opened listing failed on mobile. See
   * `readsMarket`.
   *
   * `marketAsked` is the loader this effect last ran, so a change made while
   * no such screen was showing is caught up on ARRIVAL, once, and a return
   * with nothing changed (browse → a listing → Back) asks for nothing — which
   * is what it did before, when the loader's identity simply had not moved.
   *
   * AND NOT WHILE A CHANGE OF FILTERS IS STILL ON ITS WAY. Arriving on browse
   * from a screen whose address carried another `game` changes the route and
   * the filters in one render, and the filters reach `browseQuery` one render
   * later (the burst effect above). Without `filtersSettled` the arrival asked
   * once with the address it left and again with the one it reached — counted
   * by market-requests.spec.ts. Waiting for them to agree asks once, with the
   * latest. On browse itself this changes nothing: a chip never moved the
   * loader before `browseQuery` caught up anyway.
   */
  const onMarket = readsMarket(route.name, trader);
  const filtersSettled = requestedFilters === browseQuery;
  const marketAsked = React.useRef(null);
  React.useEffect(() => {
    if (booting || !locationsReady || !onMarket || !filtersSettled) return;
    if (marketAsked.current === loadListings) return;
    marketAsked.current = loadListings;
    loadListings();
  }, [booting, locationsReady, loadListings, onMarket, filtersSettled]);
  /*
   * Resolves to whether it worked, rather than swallowing that.
   *
   * Callers that only want the data can ignore it, as they always could. The
   * poller cannot: backing off needs to know a request failed, and a loader that
   * reports success either way makes an outage indistinguishable from a quiet
   * afternoon — which is how a client keeps hammering a server that is already
   * struggling.
   */
  /*
   * A SUPERSEDED RUN REPORTS SUCCESS, and that is deliberate.
   *
   * These two resolve to whether the request worked, and the poller's backoff is
   * built on that answer. An abort is not a failure — it is this loader being
   * asked the same question again — so reporting `false` for one would make a
   * screen change look like an outage and double the polling interval for it.
   * The newer run is the one whose answer counts, and it reports for itself.
   */
  /*
   * THE BACK OFFICE'S SCOPE OVER THE TRADES, remembered the way `mineFilter`
   * remembers the stock's games: a reload with no argument keeps it, so the
   * list a shop answers from after accepting or declining is still the
   * narrowed one it was reading, and so is the one the poller refreshes. The
   * list itself rather than `mineFilter`'s shape — the trades have no search
   * box and no stale switch to merge beside it. Only the shop shell ever sets
   * it. A trader's own screens call `loadTrades()` bare, and the switch
   * between the two identities is a page load (`actAs` assigns the location),
   * so a trader's list never inherits a shop's scope; `logout` clears it with
   * the lists, for the one path that stays on the page.
   */
  const tradesScope = React.useRef([]);
  /*
   * A SHOP'S TRADES ARE IN ITS REQUESTS (shop trades view, ticket 08), and the
   * links that take it there keep the games its back office is scoped to — the
   * scope travels with the section, so the address stays one somebody can
   * send. The shell's own `go.to` does the same from inside the back office.
   */
  const actingAsShop = Boolean(trader && trader.kind === 'shop');
  const goRequests = (extra = {}) => go('dashboard', {
    sec: 'requests',
    game: tradesScope.current.length ? tradesScope.current : undefined,
    ...extra,
  });
  /*
   * OPEN TRADES ONLY, for a shop's back office (shop trades view, ticket 10):
   * its Past is paged by itself, so the list the badge and the open tabs read
   * is not a hundred trades of which most are history. Remembered like the
   * scope, so every reload asks the same question.
   */
  const tradesOpen = React.useRef(false);
  /** How many of them are over, as the open list says it; null when not asked. */
  const [pastTotal, setPastTotal] = React.useState(null);
  const loadTrades = React.useCallback((next) => {
    const run = tradesRun.begin();
    if (next) { tradesScope.current = next.games; tradesOpen.current = Boolean(next.open); }
    setTradesState('loading');
    // An empty list is no key at all — `qs` drops it — so All games is the request it always was.
    return api.trades({ game: tradesScope.current, ...(tradesOpen.current ? { open: true } : {}) }, run.signal)
      .then((r) => {
        if (!run.latest()) return true;
        setTrades(r.trades.map(api.toTradeView));
        setPastTotal(typeof r.pastTotal === 'number' ? r.pastTotal : null);
        setTradesState('ready');
        return true;
      })
      .catch((e) => {
        if (aborted(e) || !run.latest()) return true;
        setTradesState('error');
        return false;
      });
  }, []);

  const loadConversations = React.useCallback(() => {
    const run = conversationsRun.begin();
    setThreadsState('loading');
    return api.conversations(run.signal)
      .then((r) => {
        if (!run.latest()) return true;
        setThreads(r.conversations.map(api.toConversationView));
        setThreadsState('ready');
        return true;
      })
      .catch((e) => {
        if (aborted(e) || !run.latest()) return true;
        setThreadsState('error');
        return false;
      });
  }, []);


  /**
   * The next page of a list, appended.
   *
   * APPENDED, not replaced, and deduplicated by id. A "load more" that replaces
   * loses the cards somebody was already looking at; one that concatenates
   * blindly repeats a row whenever a listing moved between pages, which the
   * keyset makes rare rather than impossible — a card whose distance changed
   * because the viewer moved can legitimately appear twice.
   *
   * One at a time. The button is disabled while a page is in flight, so a
   * double-tap on a slow connection cannot ask for the same cursor twice and
   * append the same rows twice.
   */
  const loadMore = React.useCallback(async (what) => {
    const cursor = cursors[what];
    if (!cursor || loadingMore) return;
    /*
     * Null while a reload of that list is on its way: the cursor on screen is
     * then the old filter's. See `usePagedList`.
     */
    const belongs = { listings: listingsPages, mine: minePages, wants: wantsPages }[what]?.page();
    if (!belongs) return;
    /*
     * PAGED FROM THE MOMENT IT IS ASKED, not once it lands. The poll reloads
     * the first page of a list nobody has paged; a tick between the press and
     * the answer would otherwise reload it, and the reload would — rightly —
     * make the page that was asked for no longer belong.
     */
    const wasPaged = paged.current[what];
    if (what === 'listings' || what === 'mine') paged.current[what] = true;
    /*
     * THE GUARD ABOVE IS NOT ENOUGH, and this is the loader where losing the race
     * is loudest.
     *
     * `loadingMore` is state, so two calls in the same tick both read null and
     * both get through — the disabled button makes that unlikely from a mouse and
     * does nothing about a repeated key or a second call from code. This one
     * APPENDS, so a superseded page does not overwrite the list quietly the way
     * the others do: it adds its rows to the ones the newer page already added,
     * and somebody sees the same sixty cards twice.
     *
     * Deduplication by id hides half of it and cannot fix it, because the two
     * pages are legitimately different rows. The run check below is what stops a
     * discarded answer being appended at all.
     */
    const run = moreRun.begin();
    setLoadingMore(what);
    const append = (prev, incoming) => {
      const seen = new Set(prev.map((x) => x.id));
      return [...prev, ...incoming.filter((x) => !seen.has(x.id))];
    };
    try {
      if (what === 'listings') {
        const origin = point ? { lat: point[0], lng: point[1] } : {};
        // The SAME filters as the request this cursor came from. A cursor is a
        // position in a filtered list, so asking for the next page with a
        // different filter resumes from a row that list does not contain.
        const r = await api.listings({
          limit: 60, radiusM: Math.round(parseFloat(radius) * 1000), ...origin, ...browseQuery, cursor,
        }, run.signal);
        // Asked of a list that has since been asked again: not this list's page.
        if (!run.latest() || !belongs()) return;
        const next = r.listings.map(api.toListingView);
        setListings((prev) => append(prev, next));
        setCursors((c) => ({ ...c, listings: r.nextCursor ?? null }));
        /*
         * The counts are NOT touched here, and the server does not resend them.
         *
         * A count describes the filter, not the page: pressing "show more"
         * cannot change how many listings are for sale within the radius. The
         * server answers null on any page but the first, which is why this reads
         * nothing rather than reading a field that would be null.
         */
      } else if (what === 'mine') {
        // The same narrowing the cursor came from. See `mineFilter`.
        const r = await api.myListings({
          cursor,
          ...(mineFilter.current.games.length ? { game: mineFilter.current.games } : {}),
          ...(mineFilter.current.q ? { q: mineFilter.current.q } : {}),
          ...(mineFilter.current.stale ? { stale: '1' } : {}),
          ...(mineFilter.current.synced ? { synced: '1' } : {}),
          ...(mineFilter.current.sort !== 'newest' ? { sort: mineFilter.current.sort } : {}),
        }, run.signal);
        if (!run.latest() || !belongs()) return;
        setMyCards((prev) => append(prev, r.listings.map(api.toListingView)));
        setCursors((c) => ({ ...c, mine: r.nextCursor ?? null }));
      } else if (what === 'wants') {
        const r = await api.wants({ cursor }, run.signal);
        if (!run.latest() || !belongs()) return;
        setWants((prev) => append(prev, r.wants));
        setCursors((c) => ({ ...c, wants: r.nextCursor ?? null }));
      }
    } catch (e) {
      // The cursor is untouched, so the button stays and pressing it again
      // retries the same page rather than skipping it. An abort is not something
      // to apologise for: nobody asked for that page any more.
      if (run.latest() && !aborted(e)) notify(e.message, 'caution');
      // Nothing was added, so the list is as paged as it was, and the poll may refresh it as before.
      if (belongs() && (what === 'listings' || what === 'mine')) paged.current[what] = wasPaged;
    } finally {
      /*
       * Only the run that still owns the button may release it. A superseded
       * page finishing after the newer one started would otherwise clear the
       * busy state out from under a request that is still in flight, and the
       * button would offer a page that is already being fetched.
       */
      if (run.latest()) setLoadingMore(null);
    }
  }, [cursors, loadingMore, notify, point, radius, browseQuery]);

  /*
   * The poll's own request, and the one most likely to overlap itself.
   *
   * It runs every four seconds. A response slower than the interval overlaps the
   * next poll BY CONSTRUCTION rather than by accident — and coming back to a
   * hidden tab fires a tick immediately without waiting for the one in flight.
   * Two answers then land in an order nobody controls, and the older one decides
   * what the badge says.
   */
  const loadPulse = React.useCallback(() => {
    const run = pulseRun.begin();
    return api.pulse(run.signal)
      .then((r) => { if (run.latest()) setPulse({ messages: r.messages, trades: r.trades, import: r.import || null }); return true; })
      // Same as the two loaders above: an abort is a replacement, not a failure,
      // and telling the poller otherwise would back it off for no reason.
      .catch((e) => aborted(e) || !run.latest());
  }, []);

  /**
   * The wants list, which had neither guard either.
   *
   * A callback rather than the body of an effect, so it can be aborted and
   * counted like the rest — it is fetched on sign-in and paged by `loadMore`,
   * which is two writers for one list.
   */
  /**
   * Your invite code and the people who used it.
   *
   * LOADED ON DEMAND, not with the session. Most traders never open this
   * screen, and reading it is what MINTS the code — see the endpoint. Fetching
   * it for everybody at boot would hand a permanent code to every account that
   * signs in, which is precisely the column of noise the endpoint avoids.
   */
  const [referral, setReferral] = React.useState(null);
  const [referralState, setReferralState] = React.useState('idle');
  const loadReferral = React.useCallback(() => {
    const run = referralRun.begin();
    if (!trader) return Promise.resolve();
    setReferralState('loading');
    return api.myReferrals(run.signal)
      .then((r) => {
        if (!run.latest()) return;
        setReferral(r);
        setReferralState('ready');
      })
      .catch((e) => { if (run.latest() && !aborted(e)) setReferralState('error'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trader && trader.id]);

  /**
   * The draws.
   *
   * Readable by a guest, unlike everything else here: a campaign is the reason
   * somebody follows an invite link, and the landing page is read by people who
   * have no account yet. So this one does NOT bail out when `trader` is null.
   */
  /*
   * When the draws were last read. A ref, not state: nothing renders from it,
   * and re-rendering the whole app because a clock was noted would be a poor
   * trade. Starts at 0 so the first read happens rather than waiting a minute.
   *
   * Declared ABOVE `loadCampaigns`, which stamps it. It sat below and worked —
   * the callback only runs after render — but a const used before its
   * declaration reads as a bug to whoever comes next.
   */
  const lastCampaigns = React.useRef(0);
  const [campaigns, setCampaigns] = React.useState([]);
  const [campaignsState, setCampaignsState] = React.useState('idle');
  const loadCampaigns = React.useCallback(() => {
    /* Stamps its own clock, like `loadListings` does with `lastMarket`. Every
       caller then shares one gate and none of them can double up — without
       this, a cold load fetched twice, because the mount effect and the first
       navigation both counted as "nothing has happened yet". */
    lastCampaigns.current = Date.now();
    const run = campaignsRun.begin();
    setCampaignsState('loading');
    return api.campaigns(run.signal)
      .then((r) => {
        if (!run.latest()) return;
        setCampaigns(r.campaigns);
        setCampaignsState('ready');
      })
      .catch((e) => { if (run.latest() && !aborted(e)) setCampaignsState('error'); });
  // The account, because `myEntries` is about who is asking. A guest who signs
  // in must not keep reading somebody else's dashes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trader && trader.id]);

  /**
   * The draw whose result this trader has not looked at yet.
   *
   * Only ever one at a time: two reveals queued behind each other would be a
   * pile of ceremony, and campaigns are weeks apart. The oldest first, so the
   * order they are watched in is the order they happened.
   */
  const [revealId, setRevealId] = React.useState(null);
  const unrevealed = React.useMemo(() => campaigns
    .filter((c) => c.drawnAt && !c.revealed && (c.myEntries || 0) > 0)
    .slice().sort((a, b) => new Date(a.drawAt) - new Date(b.drawAt))[0] || null, [campaigns]);

  const checkResult = React.useCallback((id) => setRevealId(id), []);
  const closeReveal = React.useCallback(() => setRevealId(null), []);

  /**
   * Written when the reveal has played, so it is offered once.
   *
   * The local list is updated too rather than waiting for a refetch: the
   * overlay is still on screen when this runs, and the banner behind it must
   * not reappear the moment it closes. A failed write leaves it unrevealed,
   * which offers the moment again — the honest failure, and better than
   * swallowing a result somebody never saw.
   */
  const settleResult = React.useCallback(async (id) => {
    setCampaigns((cs) => cs.map((c) => (c.id === id ? { ...c, revealed: true } : c)));
    try { await api.markRevealed(id); }
    catch (e) { setCampaigns((cs) => cs.map((c) => (c.id === id ? { ...c, revealed: false } : c))); return; }
    /*
     * And read the draws again, because the server withholds the winners list
     * from an entrant until this write lands — it would have spoiled the
     * reveal. Without the refetch the list appeared only after a reload.
     */
    void loadCampaigns();
  }, [loadCampaigns]);

  /**
   * The draw taking entries right now, if there is one.
   *
   * Several may be open at once — they were allowed to overlap deliberately —
   * and the strip and the invite screen need ONE to talk about. The one closing
   * soonest is the one worth mentioning: it is the one somebody can still miss.
   */
  const liveCampaign = React.useMemo(() => {
    const live = campaigns.filter((c) => c.state === 'live');
    return live.slice().sort((a, b) => new Date(a.endsAt) - new Date(b.endsAt))[0] || null;
  }, [campaigns]);

  const loadWants = React.useCallback(() => {
    const run = wantsRun.begin();
    // And a page of the old list still on its way is no longer this list's.
    const shown = wantsPages.reload();
    // Beginning the run first is the point of this branch: signing out aborts
    // whatever was in flight and outranks its answer. Emptying the list is
    // `logout`'s job, which clears every list at once.
    if (!trader) { shown(); return Promise.resolve(); }
    setWantsState('loading');
    return api.wants(undefined, run.signal)
      .then((r) => {
        if (!run.latest()) return;
        setWants(r.wants);
        if (r.total != null) setWantsTotal(r.total);
        setCursors((c) => ({ ...c, wants: r.nextCursor ?? null }));
        shown();
        setWantsState('ready');
      })
      .catch((e) => { if (run.latest() && !aborted(e)) setWantsState('error'); });
  // The ACCOUNT, not the object. Editing a profile replaces `trader` without
  // changing who it is, and re-reading the wants list for a radius change would
  // be a request nothing on screen asked for.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trader && trader.id]);

  React.useEffect(() => {
    /*
     * A guest has none of these and is never asked for them, so the three states
     * are settled here rather than left at `loading` forever. Every screen that
     * reads them is behind the sign-in wall today; a permanent skeleton behind a
     * wall is the kind of thing that surfaces the day the wall moves.
     *
     * The two loaders below deliberately do not depend on `trader` themselves:
     * they are dependencies of the polling effect, and an identity that changed
     * whenever a profile was edited would restart the poller, reset its backoff
     * and fire an immediate request every time somebody moved a radius slider.
     */
    if (!trader) { setTradesState('ready'); setThreadsState('ready'); setWantsState('ready'); return; }
    loadWants();
    /* Not the trades for a shop: its back office loads them, scoped — see `loadMine`'s effect above. */
    if (trader.kind !== 'shop') loadTrades();
    loadConversations();
  }, [trader && trader.id, loadWants, loadTrades, loadConversations]);

  /**
   * The draws, once, for everybody.
   *
   * UNLIKE EVERYTHING ABOVE, this runs for a guest too — the strip that
   * announces an open draw is in the shell, and a draw is a reason to make an
   * account rather than something only account-holders may hear about.
   *
   * One request per session, not a poll. A campaign's dates are fixed weeks in
   * advance; nothing about it changes between one page and the next, and this
   * product has already paid once for a screen that asked three times per load.
   * It reruns on sign-in because `myEntries` and `youWon` are about who is
   * asking.
   */
  /*
   * AND AGAIN WHEN THE LANGUAGE CHANGES.
   *
   * Reported: switching ES/EN changed every translated string but left a
   * campaign's own title, prize and picture in the old language until a full
   * reload. Those are resolved by the SERVER — they are words an operator
   * typed, not keys in the dictionary — so the only way to change them is to
   * ask again.
   *
   * `locale` is in the dependency list for exactly that, and the request
   * carries it: the API client sets `accept-language` to whatever it is
   * rendering in, and the endpoint now trusts that over the account.
   */
  React.useEffect(() => { void loadCampaigns(); }, [loadCampaigns, locale]);

  /**
   * AND AGAIN WHEN SOMEBODY MOVES, which is the moment they expect it.
   *
   * Reported twice. The first fix gave the draws a poll, and measured, the poll
   * works: a draw that lands is on screen within about sixty-four seconds
   * without a reload. It was still reported as broken, and the report was
   * right — because navigating refetched NOTHING. Somebody who has just run a
   * draw in the back office and goes to look at it clicks through two or three
   * screens in ten seconds, sees the old state at every one of them, and
   * reloads. The page then shows it, which is exactly the evidence that says
   * "this only works with a hard refresh".
   *
   * FRESHNESS IS CHEAP HERE, unlike on the market: this is a handful of rows
   * for the whole installation, read by primary key. The gate is not about load
   * at all — it is there so one navigation is one request, because a route
   * carrying an id changes `name` and `id` together and would otherwise fetch
   * twice for a single click.
   *
   * THREE SECONDS, and it was ten first. Ten measured WORSE than it reads:
   * clicking through three screens takes about four seconds, so every one of
   * them fell inside the gate and the whole point was lost — the test showed
   * zero requests across six navigations. A gate long enough to be worth
   * having was long enough to reproduce the bug it was fixing.
   *
   * It shares `lastCampaigns` with the poll, so the two cannot double up: a
   * navigation that follows a poll tick is free, and a poll tick that follows a
   * navigation is skipped.
   */
  const NAV_CAMPAIGN_MS = 3_000;
  React.useEffect(() => {
    if (Date.now() - lastCampaigns.current < NAV_CAMPAIGN_MS) return;
    void loadCampaigns();
  // The route, and nothing else — `loadCampaigns` changing is the account
  // changing, which the effect above already covers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.name, route.id]);

  /**
   * Checking for messages and trades without being asked to.
   *
   * Chat worked but only after a refresh, and the Inbox badge only counted what
   * was true when the page loaded — so the product asked people to keep pressing
   * F5 to find out whether somebody had replied.
   *
   * Polling rather than a socket: this is a marketplace where the meaningful
   * events are somebody proposing a trade or writing a line about where to meet.
   * Those arrive minutes apart, not milliseconds, and a socket would be a
   * connection to hold open, reconnect, authenticate and scale for a latency
   * nobody would notice.
   *
   * Three things keep it cheap and polite:
   *
   *  - Only while signed in. A guest has no inbox to check.
   *  - Only while the tab is VISIBLE. A backgrounded tab polling all night is how
   *    a phone battery disappears and how a server ends up serving mostly nobody.
   *  - Immediately on becoming visible again, because coming back to the tab is
   *    exactly the moment somebody wants to know.
   */
  /*
   * Visible: often, because somebody is watching.
   * Hidden: still, but rarely — a backgrounded tab is where battery and server
   * time go to die, and the point of checking at all while hidden is the TITLE.
   * A minute late on a tab nobody is looking at costs nothing.
   */
  /**
   * Four seconds, and what pays for it.
   *
   * Close to live. A message appearing four seconds after it is sent reads as
   * the app working; twenty-five seconds reads as the app being asleep, and the
   * chime that goes with it arrives detached from the thing that caused it.
   *
   * The cost is bounded by asking for LESS rather than less often. A poll fetches
   * the two things the unread count is made of; the browse grid, your own
   * listings and the open thread are visible-tab only, and the open thread only
   * when one is open. A hidden tab is therefore two small indexed reads every
   * four seconds and nothing else.
   */
  const POLL_MS = 4000;
  /* Draws move on dates, not on seconds. See the note in `refresh`. */
  const CAMPAIGN_POLL_MS = 60_000;

  /**
   * How often the MARKET is refreshed, which is not how often the count is.
   *
   * The two were one interval and should never have been: the unread count is
   * two small per-trader reads, and browse is a distance-sorted query over every
   * active listing. Polling the second at four seconds is what took the
   * end-to-end suite from passing to timing out — each request holds a
   * connection for a couple of hundred milliseconds, and two tabs doing it were
   * enough to exhaust the pool and fail unrelated endpoints with "timeout
   * exceeded when trying to connect".
   *
   * Four seconds bought a prompt chime. Nothing about a card appearing needs to
   * be four seconds fresh — and it no longer has to be, because a want match now
   * pushes a notification rather than waiting to be noticed on screen.
   */
  const MARKET_EVERY_MS = 30000;

  /**
   * How far back off, and how far is far enough.
   *
   * The failure this exists for is the server being in trouble. Every open tab
   * polling every four seconds through an outage is a client that turns a bad
   * minute into a worse one — and the moment recovery starts, it is the load
   * that prevents it. Doubling means a page left open overnight through a broken
   * deploy asks roughly fifteen times in an hour rather than nine hundred.
   *
   * Capped, because a tab that has backed off to an hour never notices the
   * recovery. A minute is short enough that coming back is prompt and long
   * enough to be no burden.
   */
  const POLL_MAX_MS = 60000;

  React.useEffect(() => {
    if (!trader) return undefined;

    const refresh = async () => {
      /*
       * The COUNTS, not the lists.
       *
       * This used to fetch both whole lists every few seconds from every screen,
       * to show a badge and notice it going up. Measured, that was the dominant
       * load on the system: two multi-join queries per tab per four seconds is
       * what saturated the database at about a hundred concurrent tabs.
       *
       * The lists are still fetched — by the screens that draw them, below.
       */
      const ok = [await loadPulse()];

      // The list behind whichever of them is on screen, so it stays live while
      // somebody is reading it.
      if (routeRef.current === 'inbox') loadConversations();
      if (routeRef.current === 'trades') loadTrades();

      /*
       * THE DRAWS, at a minute rather than at four seconds.
       *
       * Reported: "the state of a campaign and if the draw has been done is not
       * updated while navigating the app. I need to hard refresh." My fault, and
       * the reasoning behind it was wrong in a way worth writing down: I loaded
       * these once per session because "a campaign's dates are fixed weeks in
       * advance". The DATES are. What the screen shows is not — `state` is
       * derived from the clock, so a draw goes scheduled → live → ended with
       * nothing changing in the database at all, and `drawnAt`, `myEntries` and
       * `revealed` move as the tick works.
       *
       * A MINUTE, not the four-second pulse. A campaign opens on a date and is
       * drawn on a date; a minute late is invisible, and fifteen requests a
       * minute for a row that changes twice a month is the shape of thing this
       * product has already paid for once.
       *
       * It rides the poll rather than its own timer so it inherits everything
       * that was learned there: it stops when the tab is hidden, it is prompt
       * when somebody comes back, and it cannot overlap with itself.
       */
      if (Date.now() - lastCampaigns.current > CAMPAIGN_POLL_MS) loadCampaigns();

      // Nothing below this line matters to a tab nobody is looking at.
      if (document.visibilityState !== 'visible') return ok.every(Boolean);

      /*
       * And the market itself, far less often. Somebody else posting a card near
       * you is the whole point of the product, and needing a page reload to find
       * out is the same bug as needing one to see a message — but it is not the
       * same urgency, and it is not the same cost.
       *
       * NOT while somebody is reading past the first page. Refetching replaces
       * the list with page one, so a poll arriving after "show more" threw away
       * everything it had just added. Freshness is worth less than the rows
       * somebody is currently looking at, and it comes back the moment they
       * change a filter — which reloads from the first page anyway.
       */
      /*
       * And only for the screens that SHOW the market.
       *
       * A tab sitting on the inbox was refetching the browse feed every thirty
       * seconds to render nothing with it. Measured, that is the most expensive
       * query in the system by a distance — 99% of all database time, because
       * it computes a distance for every listing in radius and then sorts them.
       * Fetching it for a screen that does not draw it is pure cost.
       *
       * Coming back to Browse no longer refetches within the window:
       * `loadListings` stamps `lastMarket` itself, so a market loaded moments
       * ago is not loaded again. It used to be left alone here, which made the
       * first tick after ANY mount immediately due — including the one that
       * follows the mount's own load, which is a duplicate request on every
       * cold page load.
       *
       * Browse only, AND `readsMarket`: the load effect loads on every market
       * screen, this refreshes on browse alone, and asking the same gate keeps
       * this one a subset of that one.
       */
      const showsMarket = routeRef.current === 'browse' && readsMarket(routeRef.current, traderRef.current);
      const showsMine = routeRef.current === 'you';
      const now = Date.now();
      if ((showsMarket || showsMine) && now - lastMarket.current >= MARKET_EVERY_MS) {
        lastMarket.current = now;
        if (showsMarket && !paged.current.listings) loadListings();
        if (showsMine && !paged.current.mine) loadMine();
      }
      /*
       * And the conversation actually ON SCREEN — which means checking that it
       * still is.
       *
       * `GET /api/conversations/:id` marks the conversation READ, because
       * looking at a conversation is what reading it means. That makes this poll
       * dangerous the moment the thread is no longer on screen: `openThread` was
       * cleared only on sign-out, so after visiting a conversation once, every
       * later tick kept fetching it — and kept marking it read — while the
       * person was somewhere else entirely.
       *
       * Reported as "the count appears then disappears, and stays gone after a
       * refresh". It was gone: the messages really had been marked read, by us,
       * four seconds after they arrived.
       */
      const open = routeRef.current === 'inbox' ? openThreadRef.current : null;
      if (open && open.id) {
        api.conversation(open.id)
          .then((r) => setOpenThread((prev) => (prev.id === open.id
            ? { ...prev, messages: r.messages.map(api.toMessageView), cards: r.listings || {} }
            : prev)))
          .catch(() => undefined);
      }
      return ok.every(Boolean);
    };

    /*
     * A timer that schedules the NEXT poll when the last one finishes, rather
     * than an interval.
     *
     * `setInterval` fires on a clock regardless of whether the previous request
     * has come back, so a server taking longer than the period gets a second
     * request while it is still answering the first — which is precisely when it
     * can least afford one, and precisely how a slow server becomes a stuck one.
     * At four seconds that stopped being theoretical.
     *
     * It is also what makes the backoff possible at all: an interval cannot
     * change its own period.
     */
    let stopped = false;
    let timer = null;
    let delay = POLL_MS;

    const loop = async () => {
      const ok = await refresh();
      // Reset on success, double on failure. The next poll is scheduled from
      // here, so a slow answer pushes the following one out rather than
      // overlapping with it.
      delay = ok ? POLL_MS : Math.min(delay * 2, POLL_MAX_MS);
      if (!stopped) timer = setTimeout(loop, delay);
    };
    timer = setTimeout(loop, POLL_MS);

    const onVisibility = () => {
      // Coming back to the tab is exactly when somebody wants to know — and it
      // is also a moment worth trusting again, because the person has just told
      // us they are here. Clearing the backoff means a tab that backed off
      // during an outage is prompt the moment it is looked at.
      if (document.visibilityState !== 'visible') return;
      clearTimeout(timer);
      delay = POLL_MS;
      void loop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [trader && trader.id, loadPulse, loadTrades, loadConversations, loadListings, loadMine]);

  /**
   * The tab title carries what is waiting for you.
   *
   * "(3) Cardboardmarket" — the one place a browser will show you something while
   * you are looking at a different tab entirely, with no permission prompt, no
   * notification API, and no dependence on the operating system. It costs nothing
   * and works everywhere, which is why it comes first.
   */
  /*
   * Counted separately, because the chime has to know WHICH went up.
   *
   * They are added together for the title, where one number is the whole point.
   * They cannot be added together for the sound: whether a noise is welcome
   * depends on whether the person is looking at the thing that changed, and
   * "the inbox" and "your trades" are different screens.
   */
  /*
   * The list ONLY on the screen that owns it; the pulse everywhere else.
   *
   * The first version of this preferred the list whenever one had been loaded,
   * which is wrong in a way that took a test to find: a list is only refreshed
   * on its own screen, so once somebody had visited the inbox, `threads` stayed
   * non-empty forever and the badge went on counting a snapshot taken minutes
   * ago — ignoring the pulse that was arriving every four seconds with the real
   * answer. Messages arrived and the badge stayed at zero.
   *
   * On its own screen the list is genuinely better: it is being refetched, and
   * it drops to zero optimistically the moment a thread is opened, before any
   * request comes back.
   */
  const onInbox = route.name === 'inbox';
  const onTrades = route.name === 'trades';
  const unreadMessages = onInbox
    ? threads.reduce((n, x) => n + (x.unread || 0), 0)
    : (pulse ? pulse.messages : 0);
  const waitingTrades = onTrades
    ? trades.filter((x) => x.can && (x.can.accept || x.can.complete)).length
    : (pulse ? pulse.trades : 0);
  const waiting = unreadMessages + waitingTrades;

  React.useEffect(() => {
    document.title = waiting > 0 ? `(${waiting}) Cardboardmarket` : 'Cardboardmarket';
  }, [waiting]);

  /**
   * A short sound when something new arrives while you are looking elsewhere.
   *
   * Deliberately narrow, because an unexpected noise from a background tab is
   * obnoxious when it is wrong:
   *
   *  - Only when the count goes UP. Reading a message must never make a sound.
   *  - Only while the tab is hidden. If you are looking at the page, you can see
   *    it; a sound would be telling you something you already know.
   *  - Never on the first measurement, or arriving with two unread messages would
   *    greet you with a beep for news that is hours old.
   *  - Off if the trader turned it off.
   *
   * WebAudio rather than an audio file: two hundred milliseconds of sine wave
   * needs no asset, no network request and no decoding. Browsers block audio until
   * somebody has interacted with the page, and that block is correct — so this
   * fails silently rather than trying to work around it.
   */
  /*
   * A short sound when something arrives that you are not looking at.
   *
   * "Not looking at" is the whole rule, and it used to mean only "the tab is
   * hidden". That was too narrow: sitting on Browse with the tab in front of you
   * while a message lands is exactly the case where a count going up in the
   * corner is missed — reported that way, and correct. Being in the app is not
   * the same as watching the inbox.
   *
   * So the test is now per kind, against the screen that would have shown it:
   * a message is silent only while the inbox is open, a trade only while the
   * trades screen is. Anywhere else, and everywhere while hidden, it sounds.
   *
   * The rest stays as narrow as it was, because a noise that is wrong is
   * obnoxious and hard to trace back to the page that made it:
   *
   *  - Only when the count goes UP. Reading a message must never make a sound.
   *  - Never on the first measurement, or arriving with two unread messages
   *    would greet you with a chime for news that is hours old.
   *  - Off if the trader turned it off.
   */
  const lastCounts = React.useRef(null);
  /*
   * WHETHER THIS DEVICE WILL BE TOLD ANOTHER WAY, which decides whether the page
   * should make a sound while it is out of sight. See the chime below.
   *
   * A ref rather than state: nothing renders from it, and it is read inside an
   * effect that must not re-run when it changes.
   *
   * Re-asked when the page comes back, because the answer moves — somebody turns
   * the switch on in another tab, or the server drops a device that stopped
   * working. The cost is one cheap local read of the subscription; it does not
   * go to the network unless it finds one to resync.
   */
  const notified = React.useRef(false);
  React.useEffect(() => {
    const ask = () => { void currentState().then((state) => { notified.current = Boolean(state.subscribed); }); };
    ask();
    document.addEventListener('visibilitychange', ask);
    return () => document.removeEventListener('visibilitychange', ask);
  }, []);
  React.useEffect(() => {
    /*
     * Nothing is a baseline until it has actually been measured.
     *
     * Returning before the ref is set — rather than after — is the whole fix.
     * While the pulse is unknown these counts are zero because nothing has been
     * asked, not because there is nothing waiting, and recording that as the
     * baseline makes the first real answer look like an arrival.
     */
    if (!pulse) return;
    const previous = lastCounts.current;
    lastCounts.current = { messages: unreadMessages, trades: waitingTrades };
    if (previous === null) return;
    if (!settings.sound) return;

    const hidden = document.visibilityState !== 'visible';
    // The screen that would have shown it. Watching it is the only reason to
    // stay quiet — the badge and the row are already saying so.
    const watching = (name) => !hidden && route.name === name;

    const newMessage = unreadMessages > previous.messages && !watching('inbox');
    const newTrade = waitingTrades > previous.trades && !watching('trades');
    if (!newMessage && !newTrade) return;

    /*
     * ONE SOUND PER EVENT, AND THE NOTIFICATION WINS WHERE THERE IS ONE.
     *
     * REPORTED: "I still hear the chime when the app is in the background and
     * that shouldn't happen. Specifically, I heard it after discarding a push
     * notification." And then, when I blamed a stale browser subscription: "But
     * I've uninstalled every version of the app and only have the last one you
     * sent me."
     *
     * He was right and I was wrong twice over. This chime is OURS — WebAudio,
     * from this page, thirty lines up. In the Android app the page keeps running
     * while the app is in the background, so a message made the phone sound
     * twice: the notification, and then this, on the next pulse. Dismissing the
     * notification did not stop the second one, which is why it seemed to arrive
     * BECAUSE of the dismissal.
     *
     * So where the device will be notified properly, the page stays quiet while
     * it is out of sight. The chime keeps the job it was written for — you are
     * looking at the app, on another screen, and something arrived — and hands
     * back the one it was never meant to have.
     *
     * NOT SIMPLY "NEVER WHEN HIDDEN", because a browser tab with no
     * subscription would then go silent, and that is the case this feature was
     * built for in the first place.
     */
    if (hidden && notified.current) return;

    void chime();
  }, [pulse, unreadMessages, waitingTrades, route.name, settings.sound]);


  /**
   * The browse the trader was last looking at, so coming back comes back to IT.
   *
   * Browse's filters live in the address — that is what makes a search
   * shareable — and every way back to it was built as `go('browse')` with no
   * parameters, which is the address of the whole market. So filtering to
   * Japanese, opening a card and pressing Browse in the header threw the filters
   * away and refetched everything, while the browser's own back button restored
   * them perfectly. Two buttons a centimetre apart, doing different things,
   * neither of them labelled with the difference.
   *
   * Remembered rather than pushed into every call site: the header, the tab bar,
   * the footer, the wordmark and the listing screen's empty state all navigate to
   * browse, and a filter set that five places had to remember to carry is a
   * filter set that four of them will eventually drop.
   *
   * A ref, not state — nothing renders from it, and re-rendering the whole app
   * because a route was noted would be a poor trade. It is deliberately NOT
   * persisted: a reload lands on a URL, and the URL is then the only honest
   * answer to what is being shown.
   */
  const lastBrowse = React.useRef(null);
  React.useEffect(() => {
    if (route.name !== 'browse') return;
    // Everything except the screen's own name; what is left is the filters.
    const { name, ...filters } = route;
    lastBrowse.current = filters;
  }, [route]);

  /*
   * `go` is `navigate` with that one memory attached.
   *
   * Only when the caller passed NO parameters, which is the difference between
   * "take me back to browsing" and "show browse with exactly this". The wants
   * banner and the filter controls pass what they mean and are untouched; so is
   * `navigate`, which every screen can still reach for the unfiltered market.
   *
   * The ordering rides along in the remembered route as `sort` (see
   * `linkedSort`), and is also store state, which the store never unmounts.
   */
  const go = React.useCallback((name, params, options) => {
    if (name === 'browse' && params === undefined && lastBrowse.current) {
      return navigate('browse', lastBrowse.current, options);
    }
    return navigate(name, params ?? {}, options);
  }, [navigate]);

  /**
   * Whether going back stays inside the app.
   *
   * `history.length > 1` is the only signal available: a listing opened from a
   * shared link is the first entry, and offering "Back" there would take someone
   * out of the app entirely. Read once at mount — it only grows.
   */
  const [canBack] = React.useState(() => window.history.length > 1);

  /**
   * Guards an action that needs an account.
   *
   * Returns true when the caller may proceed. When it returns false it has already
   * sent the visitor to sign up, remembering what they were doing so the app can
   * say why rather than dumping them on a form with no explanation.
   */
  const requireAuth = React.useCallback((what, options = {}) => {
    if (trader) return true;
    setIntent(what || null);
    // `replace` exists because of a real bug: guarding a personal screen put THAT
    // screen in history, so leaving the form went back to it, the guard fired
    // again, and the visitor bounced. A blocked route must not be somewhere you
    // can return to — and with real history that now matters for the browser's
    // own back button too, not just ours.
    navigate('onboarding', {}, { replace: Boolean(options.replace) });
    return false;
  }, [trader, navigate]);

  /**
   * The code somebody arrived with, held until they finish signing up.
   *
   * It lives in the store rather than in the onboarding screen's own state
   * because the two are separated by a navigation: the landing page reads the
   * code out of the address, and the form that spends it is a different screen.
   *
   * CLEARED WHEN IT IS SPENT, and never re-applied. A referral can be recorded
   * once in a person's life, so a code left lying around here can only ever be
   * sent again to be refused — and the refusal would arrive on somebody's
   * second account, which is exactly the shape of thing that looks like a bug.
   */
  const [inviteCode, setInviteCode] = React.useState(null);

  const signup = async (email, password, displayName) => {
    const r = await api.signup(email, password, displayName, inviteCode);
    track('signup');
    setInviteCode(null);
    setTrader(r.trader);
    return r;
  };
  /**
   * Screens you must never be returned to.
   *
   * Signing in normally means somebody was INTERRUPTED — they tapped a listing,
   * were asked who they were, and want to land back on that listing. `back()`
   * is right for that and stays.
   *
   * These three are not that. Each is the end of a one-shot errand and is dead
   * the moment it is left: a reset link has been spent, a confirmation link has
   * been spent, and the forgot form has already sent its mail. Going back to one
   * shows a screen that cannot work twice — which is exactly what happened:
   * reset a password, sign in with it, and land on `/reset?token=…` again,
   * holding a token the server had already consumed.
   */
  const ONE_SHOT_ROUTES = new Set(['reset', 'verify', 'forgot']);

  const login = async (email, password) => {
    const r = await api.login(email, password);
    track('signin');
    setTrader(r.trader);
    setIntent(null);
    /*
     * Back where they were, not to a generic home — they were interrupted.
     * Unless where they were is a page that cannot be returned to, in which
     * case browse is the honest landing and there is nothing behind them worth
     * recovering.
     *
     * Decided on the route we LEFT rather than on a returnUrl in the query
     * string: the app already knows where somebody came from, and a redirect
     * target that travels in a URL is a thing to validate for ever after.
     */
    if (ONE_SHOT_ROUTES.has(cameFrom.current)) go('browse');
    else back();
    await loadListings();
    return r;
  };
  const logout = async () => {
    try { await api.logout(); } catch (e) { /* the cookie is gone either way */ }
    // Everything personal, not just the obvious three. Leaving trades, threads or
    // your own listings in memory means the next person to open this tab sees the
    // previous account's business for as long as the page lives — which on a
    // shared device is the whole point of signing out.
    setTrader(null);
    setMyShop(null);
    setListings([]); setWants([]); setMyCards([]);
    setTrades([]); setThreads([]); setOpenThread({ id: null, messages: [], loading: false });
    // And the scope a shop had over its stock and its trades, so the next account's lists are whole.
    mineFilter.current = { games: [], q: '', stale: false, synced: false, sort: 'newest' };
    tradesScope.current = [];
    tradesOpen.current = false;
    setPastTotal(null);
    setPointState(null);
    // The list of places somebody trades from is as personal as anything here, and
    // signing out on a shared device is exactly when it must not linger.
    setLocations([]);
    try { window.localStorage.removeItem('cbm.point'); } catch (e) { /* nothing to clear */ }
    go('onboarding');
  };

  /**
   * PUTTING ON THE SHOP, OR TAKING IT OFF.
   *
   * A FULL PAGE LOAD, DELIBERATELY. The session changes which account every
   * request answers for, so the moment it flips, everything this store is
   * holding belongs to somebody else: the listings, the wants, the trades, the
   * conversations, the unread count, the saved places, the point every distance
   * on screen was measured from. Clearing them by hand is the same list
   * `logout` keeps — and it is the list that grows every time a feature is
   * added, where the one that is forgotten is always the interesting one.
   *
   * So this navigates rather than setting state: the app boots, asks `/api/me`
   * who it is now, and nothing stale can survive it. It costs one page load, in
   * exchange for not having a second place that has to remember everything
   * personal.
   */
  const actAs = async (shopId, to = '/') => {
    await api.actAsShop(shopId);
    window.location.assign(to);
  };

  /**
   * The end of onboarding is a real account plus the settings that make the radius
   * mean something. The point and radius are sent together: a radius without a
   * centre filters nothing.
   */
  const finishOnboarding = async ({ locationLabel } = {}) => {
    // `languages` travels with `games` — the same kind of answer, given on the
    // same step. An empty array is a legitimate value meaning "all languages",
    // so it is sent rather than omitted: leaving it out would say nothing where
    // the trader may have deliberately cleared a preference on the way through.
    const patch = { radiusM: Math.round(parseFloat(radius) * 1000), games, languages };
    if (point) { patch.lat = point[0]; patch.lng = point[1]; }
    /*
     * The name for the place they just marked, and ONLY when they typed one.
     *
     * Sending the placeholder's words instead would put the server's default back
     * through the client, in whichever language the browser happened to be showing
     * — and then a trader who switched language halfway through signup would end
     * up with a location named in the other one. Omitted, the server names it, in
     * the language the ACCOUNT is set to.
     */
    const named = (locationLabel || '').trim();
    if (point && named) patch.locationLabel = named;
    // Which way people actually set a location decides whether the map is worth
    // the weight it costs.
    track('location.set', { value: point ? 2 : 1 });
    track('radius.change', { value: Math.round(parseFloat(radius)) });
    const r = await api.updateMe(patch);
    setTrader(r.trader);
    setIntent(null);
    back();
    await loadListings();
    notify(t("Radius set. These are the cards within it."));
  };
  const replayOnboarding = () => go('onboarding');

  /**
   * Where you are, and how far you will go — kept on the ACCOUNT, not just here.
   *
   * These were plain React state. Onboarding persisted them once and nothing ever
   * did again, so changing your location later moved a pin on a map and told the
   * server nothing. Every consequence followed from that: the server has no point
   * to measure from, so it cannot filter by distance and returns everything; every
   * distance comes back null, so no listing shows how far away it is. The product
   * is proximity, and proximity was switched off for anybody who set their location
   * anywhere except the one screen that happened to save it.
   *
   * The local value changes first so the map does not lag behind the finger, then
   * the account catches up and the list is re-read with the server's own opinion.
   */
  const persistLocation = React.useCallback(async (patch) => {
    if (!trader) return;
    try {
      const r = await api.updateMe(patch);
      setTrader(r.trader);
      /*
       * No reload here, deliberately.
       *
       * The effect below already re-reads the list whenever `point` or `radius`
       * changes, because both are dependencies of loadListings. Calling it here as
       * well fired a SECOND request — and worse, the wrong one: this closure
       * captured loadListings from the render before the change, so it asked for
       * the OLD radius. Changing 5 km to 15 km sent one request for 15,000 and one
       * for 5,000, and whichever answered last won.
       */
    } catch (e) {
      // Worth saying out loud: silently keeping a local-only location is exactly
      // the failure this function exists to end.
      notify(t("Could not save that. Your location may not be up to date."), 'caution');
    }
  }, [trader, notify, t]);

  const setPoint = React.useCallback((next) => {
    setPointState(next);
    try {
      if (next) window.localStorage.setItem('cbm.point', JSON.stringify(next));
      else window.localStorage.removeItem('cbm.point');
    } catch (e) {
      // A browser refusing storage is not a reason to refuse the location.
    }
    if (next) persistLocation({ lat: next[0], lng: next[1] });
  }, [persistLocation]);

  const setRadiusPersisting = React.useCallback((next) => {
    setRadius(next);
    const km = parseFloat(next);
    if (Number.isFinite(km)) persistLocation({ radiusM: Math.round(km * 1000) });
  }, [persistLocation]);

  /**
   * Taking the server's word for the whole location state at once.
   *
   * Every location route answers with the refreshed account AND the whole list,
   * and this adopts both together. Separately would be worse than useless here:
   * `home_point` moves on almost all of them — switching, moving a pin, deleting
   * the active one and being promoted onto another — so a client that updated the
   * list and kept its old point would show a chip naming one place while every
   * distance on the screen behind it was still measured from the last one.
   *
   * The device's own copy goes with it, for the same reason it is written when a
   * guest drops a pin: it is what fills the gap before /api/me answers on the next
   * load, and a stale one there would survive a reload.
   */
  const adoptLocationState = React.useCallback((answer) => {
    if (!answer) return;
    const home = answer.trader && answer.trader.homePoint;
    setTrader(answer.trader);
    setLocations(answer.locations || []);
    setPointState(home ? [home.lat, home.lng] : null);
    try {
      if (home) window.localStorage.setItem('cbm.point', JSON.stringify([home.lat, home.lng]));
      else window.localStorage.removeItem('cbm.point');
    } catch (e) {
      // A browser refusing storage is not a reason to refuse the location.
    }
  }, []);

  /*
   * The four things you can do to a saved location.
   *
   * Each is one request and each rethrows, because every one of them is somebody
   * pressing a button and waiting: a save that fails silently leaves the screen
   * disagreeing with the account, which is precisely the failure `persistLocation`
   * was written to end. The caller shows the message.
   *
   * Adding makes the new one active, which is what somebody who has just dropped a
   * pin and named it expects — the same act that has always set a location, with a
   * word attached.
   */
  const addLocation = React.useCallback(async ({ label, lat, lng }) => {
    adoptLocationState(await api.addLocation({ label, lat, lng }));
  }, [adoptLocationState]);

  const editLocation = React.useCallback(async (id, patch) => {
    adoptLocationState(await api.updateLocation(id, patch));
  }, [adoptLocationState]);

  const activateLocation = React.useCallback(async (id) => {
    adoptLocationState(await api.updateLocation(id, { active: true }));
  }, [adoptLocationState]);

  const removeLocation = React.useCallback(async (id) => {
    adoptLocationState(await api.removeLocation(id));
  }, [adoptLocationState]);

  /**
   * The games you play, saved to the account.
   *
   * `setGames` existed and only ever changed the value in memory, so onboarding
   * was the one and only chance to answer — pick up a new game a year later and
   * there was nothing anywhere that would take the correction. The endpoint has
   * accepted `games` on PATCH /api/me since it was written; nothing called it.
   *
   * Optimistic, because this is a set of toggles: waiting for a round trip before
   * a tag looks selected makes the control feel broken. If the save fails the
   * value is put back and the person is told, rather than the screen quietly
   * disagreeing with the server.
   */
  /**
   * One email preference, saved to the account.
   *
   * Rethrows rather than swallowing: the switch that called this needs to know,
   * because it moved optimistically and has to move back. A setting that silently
   * fails to save is worse than one that fails loudly — the screen then disagrees
   * with what the server will actually do, and the person only finds out by not
   * receiving an email they thought they had asked for.
   */
  const setNotifyPreference = React.useCallback(async (key, value) => {
    const updated = await api.updateMe({ [key]: value });
    setTrader(updated.trader);
  }, []);

  /*
   * Saving a way to be reached.
   *
   * Returns the whole response rather than nothing, because the caller reads the
   * stored value back out of it to show the number in its readable form. It used
   * to carry an `assumedCountry` flag as well — the server would put `34` in
   * front of a bare nine digits and the screen had to confess it. The server no
   * longer guesses a country at all, so there is nothing left to confess.
   */
  const saveContact = React.useCallback(async (channel, raw) => {
    const updated = await api.updateMe({ [channel]: raw });
    setTrader(updated.trader);
    return updated;
  }, []);

  const setGamesPersisting = React.useCallback(async (next) => {
    const before = games;
    setGames(next);
    try {
      await api.updateMe({ games: next });
    } catch (e) {
      setGames(before);
      notify(t("Those games could not be saved."));
    }
  }, [games, notify, t]);

  /**
   * The card languages you prefer, saved to the account.
   *
   * The twin of `setGamesPersisting` above, optimistic for the same reason: a set
   * of toggles that waits for a round trip before a chip looks selected feels
   * broken. On failure the value goes back and the person is told, rather than
   * the screen quietly disagreeing with the server.
   */
  const setLanguagesPersisting = React.useCallback(async (next) => {
    const before = languages;
    setLanguages(next);
    try {
      await api.updateMe({ languages: next });
    } catch (e) {
      setLanguages(before);
      notify(t("Those languages could not be saved."));
    }
  }, [languages, notify, t]);

  /**
   * Taking a listing down.
   *
   * The endpoint has always existed and the API client has always had a method
   * for it; nothing ever called either. So a card could be posted and never
   * unposted — the only exit was completing a trade for it.
   *
   * A withdrawal is not a deletion: the row stays, because a trade may reference
   * it and somebody else's history should not develop a hole when you tidy up.
   * It stops being findable, which is what "remove it" means to the person asking.
   */
  const withdrawListing = async (id) => {
    const before = listings;
    /*
     * Removed from BOTH lists, because the card is in both.
     *
     * `listings` is the browse feed; `myCards` is the selling tab, which is the
     * screen somebody is actually looking at when they take one down. Updating
     * only the feed meant the card stayed on screen until a reload — the action
     * appeared to do nothing, which is how somebody presses it twice.
     */
    const beforeMine = myCards;
    // Optimistic, because the grid it disappears from is the thing being acted on
    // and a spinner there reads as a failure.
    setListings((ls) => ls.filter((l) => l.id !== id));
    setMyCards((cs) => cs.filter((c) => c.id !== id));
    try {
      await api.withdrawListing(id);
      notify(t("Listing taken down. Nobody sees it now."));
    } catch (e) {
      setListings(before);
      setMyCards(beforeMine);
      notify(e.message, 'caution');
    }
  };

  /**
   * Taking several off the market at once.
   *
   * Both lists again, and optimistically, for the reasons above: the grid being
   * acted on is the one somebody is looking at.
   *
   * THEN THE SHELF IS RE-READ. `myCardsTotal` counts listings past the first
   * page and the cursor describes a list that just changed length — neither can
   * be derived from the cards this dropped. The same thing `setFollowingInBulk`
   * does after writing, and `removeWants`.
   *
   * The count in the message is the SERVER's. A listing already withdrawn in
   * another tab is not an error and not one this took down.
   */
  const withdrawListings = async (ids) => {
    if (!ids.length) return 0;
    const before = listings;
    const beforeMine = myCards;
    const chosen = new Set(ids);
    setListings((ls) => ls.filter((l) => !chosen.has(l.id)));
    setMyCards((cs) => cs.filter((c) => !chosen.has(c.id)));
    try {
      const r = await api.withdrawListings(ids);
      loadMine();
      notify(r.withdrawn === ids.length
        ? t("{n} listings taken down. Nobody sees them now.", { n: r.withdrawn })
        : t("{n} of the {asked} you chose are down. The rest were already off the market.",
            { n: r.withdrawn, asked: ids.length }));
      return r.withdrawn;
    } catch (e) {
      setListings(before);
      setMyCards(beforeMine);
      notify(e.message, 'caution');
      throw e;
    }
  };

  /**
   * Editing a listing: the notes, how many copies, or both.
   *
   * The refusal this has to handle well is `stock_below_committed`. It is not a
   * failure in the sense a toast is for — the seller asked for something
   * reasonable and the answer is "not while three copies are promised to these
   * three people". A toast saying so and vanishing leaves them with a number
   * and nowhere to go, so the error is rethrown WITH the server's body attached
   * and the dialog renders the trades. See you.jsx.
   */
  /*
   * Market-following across many listings at once.
   *
   * RELOADS RATHER THAN PATCHING THE LIST IN PLACE. A single edit knows exactly
   * which row changed and can update it; this one can move every price the
   * seller has, and the new numbers come from the server's rule rather than
   * from anything this client could compute. Rebuilding the list from the
   * answer is the only version that cannot show a stale price.
   */
  /**
   * MANY PRICES AT ONCE, for the shop's back office.
   *
   * Reports what actually moved, and what did not: a listing following the
   * market keeps its price, because the nightly statement owns it, and a seller
   * told "done" over a shelf that did not change has been lied to by a number.
   */
  /*
   * The cards a bulk change left alone because the shop's CardTrader sync owns
   * their price — one more sentence after whatever the change did say, or
   * nothing. Said for the same reason the following ones are: "done" over a
   * shelf that partly did not move is a number that lies.
   */
  const syncedSkipped = (r) => (r.managedOnCardtrader > 0
    ? ` ${t("{skipped} are managed on CardTrader, so their prices stay as they are.", { skipped: r.managedOnCardtrader })}`
    : '');

  const bulkPrice = async (selection) => {
    try {
      const r = await api.bulkPrice(selection);
      loadMine();
      notify(
        (r.following > 0
          ? t("{n} prices changed. {skipped} follow Cardmarket, so their prices stay as they are.",
              { n: r.changed, skipped: r.following })
          : t("{n} prices changed.", { n: r.changed }))
        + syncedSkipped(r),
      );
      return r;
    } catch (e) {
      notify(e.message, 'caution');
      throw e;
    }
  };

  const setFollowingInBulk = async (selection) => {
    try {
      const r = await api.setFollowingInBulk(selection);
      loadMine();
      /*
       * The count is what was actually done, and the skipped ones are said out
       * loud. "Done" over a number the seller did not get is how somebody comes
       * to believe a card is tracking the market when it never can.
       */
      notify(
        !selection.followsMarket
          ? t("{n} listings no longer follow Cardmarket.", { n: r.following })
        : (r.withoutCatalogue > 0
          ? t("{n} listings now follow Cardmarket. {skipped} are not in the catalogue, so their prices stay as they are.",
              { n: r.following, skipped: r.withoutCatalogue })
          : t("{n} listings now follow Cardmarket.", { n: r.following }))
          + syncedSkipped(r),
      );
      return r;
    } catch (e) {
      notify(e.message, 'caution');
      throw e;
    }
  };

  const editListing = async (id, patch) => {
    try {
      const r = await api.editListing(id, patch);
      const view = api.toListingView(r.listing);
      setMyCards((cs) => cs.map((c) => (c.id === id ? view : c)));
      // Named after what actually changed. "Listing updated" for a note-only
      // edit is vaguer than the message this screen has always shown, and
      // vaguer for no reason: the caller sends only the fields it changed.
      notify(patch.quantity === undefined ? t("Notes updated.")
        : patch.note === undefined ? t("Copies updated.")
        : t("Listing updated."));
      return view;
    } catch (e) {
      /*
       * Silent here, deliberately, and only for this one code. Everything the
       * seller needs to see is a list the dialog is about to draw; a toast on
       * top of it would say less than the screen already does and cover part
       * of it while doing so.
       */
      if (e && e.code !== 'stock_below_committed') notify(e.message, 'caution');
      throw e;
    }
  };

  const addWant = async (want) => {
    if (!requireAuth('add a want')) return;
    try {
      const r = await api.addWant(want);
      // The API answers with the id and the name the list reads it by (the
      // want's own language, or null for English), so the row is assembled here
      // from what was sent plus what came back — no invented fields.
      setWants((w) => [{ id: r.id, localTitle: r.localTitle ?? null, ...want, note: want.note || null, matchesWithinRadius: 0 }, ...w]);
      notify(t("Want added. We’ll ping you when it appears within your radius."));
    } catch (e) { notify(e.message, 'caution'); }
  };
  const removeWant = async (id) => {
    const before = wants;
    setWants((w) => w.filter((x) => x.id !== id));
    try { await api.removeWant(id); }
    catch (e) { setWants(before); notify(e.message, 'caution'); }
  };

  /**
   * Removing several at once, which is the fifty-first want's problem.
   *
   * The rows go immediately, as they do for one want: the person chose them and
   * watching a list think about it is worse than being wrong for 200ms.
   *
   * THEN THE LIST IS RE-READ, rather than patched. Two things this cannot
   * compute from the rows it dropped: `wantsTotal` counts wants past the first
   * page, and the cursor describes a list that just changed length. The Selling
   * tab's bulk edit does the same after writing.
   *
   * The count in the message is the SERVER's. A want deleted in another tab is
   * not an error and not a row this removed — saying "10 removed" over nine is
   * how somebody comes to believe a list is shorter than it is.
   */
  const removeWants = async (ids) => {
    if (!ids.length) return 0;
    const before = wants;
    const chosen = new Set(ids);
    setWants((w) => w.filter((x) => !chosen.has(x.id)));
    try {
      const r = await api.removeWants(ids);
      loadWants();
      notify(r.removed === ids.length
        ? t("{n} wants removed.", { n: r.removed })
        : t("{n} of the {asked} you chose are gone. The rest had already been removed.",
            { n: r.removed, asked: ids.length }));
      return r.removed;
    } catch (e) {
      setWants(before);
      notify(e.message, 'caution');
      throw e;
    }
  };

  /**
   * Post a card: upload its photographs, then create the listing from them.
   *
   * This order is what makes "a listing must have a picture" true at creation
   * rather than checked afterwards. The files go up first and belong to the
   * trader; the create request names them, and claims them in the same
   * transaction that inserts the listing. A listing without a photograph is not
   * something the API can be asked for.
   *
   * If an upload fails, nothing is created — the form still holds what was
   * typed, which is the failure worth optimising for: re-picking a photo is
   * easy, re-typing a card is not.
   */
  const publishListing = async (draft, photos = []) => {
    if (!requireAuth('post a card')) return;

    const photoIds = [];
    for (const original of photos) {
      /*
       * SHRUNK BEFORE IT LEAVES THE PHONE.
       *
       * Measured: a 9 MB phone photo becomes about 300 KB, and the seller's wait
       * goes from seconds to under one on a mobile connection. The server does
       * its own resizing regardless — this is an optimisation, not a promise —
       * and `shrinkPhoto` returns the original on any failure, so a browser that
       * cannot decode HEIC gets today's behaviour rather than a refused post.
       */
      const file = await shrinkPhoto(original);
      try {
        const up = await api.uploadPhoto(file);
        photoIds.push(up.photo.id);
      } catch (e) {
        notify(
          e && e.code === 'too_large' ? t("“{name}” is too big. 12 MB is the limit.", { name: file.name })
          : e && (e.code === 'unsupported_type' || e.code === 'not_an_image')
            ? t("“{name}” is not a photo we can read.", { name: file.name })
          : e && e.code === 'uploads_unavailable' ? t("Photo uploads are switched off right now.")
          : t("“{name}” did not upload.", { name: file.name }),
        );
      }
    }
    try {
      const r = await api.createListing({
        title: draft.title,
        setName: draft.set || undefined,
        game: draft.game,
        grade: draft.condition,
        // Always sent, and never as an empty string: the form will not submit
        // without a pick, and the API requires one since migration 050. The
        // `|| undefined` that used to be here meant "nobody has said", which is
        // no longer a state a card can be in.
        language: draft.language,
        mode: draft.mode,
        // Already cents when it arrives, and read from the box by the same parse
        // the form's own preview used — so what was on screen and what is stored
        // cannot differ. This used to be `Math.round(draft.price * 100)` on a
        // euro float, which is where a decimal price died: `+"0,57"` is NaN, and
        // a NaN reaches the schema as a null it refuses.
        priceCents: draft.mode === 'sale' && draft.priceCents > 0 ? draft.priceCents : undefined,
        // Sent only when it is more than one, so an ordinary single-card post
        // carries the same body it always did and the column's own default is
        // what answers.
        ...(draft.quantity && draft.quantity > 1 ? { quantity: draft.quantity } : {}),
        note: draft.note || undefined,
        /*
         * Foil, sent every time — including when it is false.
         *
         * `quantity` above is sent only when it is interesting, because an
         * absent quantity and a quantity of one are the same request. This is
         * not that: `false` is the seller's answer to a question the form asked
         * them, and the schema accepts it. Sending it always is also what keeps
         * an edit honest the day this form learns to edit rather than only
         * create — an omitted false would read as "no change" instead of "no".
         */
        foil: !!draft.foil,
        sourceProductId: draft.sourceProductId,
        /*
         * FOLLOWING CARDMARKET FROM THE FIRST DAY — migration 091.
         *
         * Sent only when the seller asked for it, so an ordinary post carries
         * the body it always did. The API refuses following without a
         * catalogue product, which is the same condition the form uses to
         * decide whether to offer the switch at all — so the refusal is a
         * backstop rather than something a seller can reach.
         *
         * `marketAdjust` is sent whenever following is on, null included:
         * null is not "no answer" here, it is the first of the three options
         * — the low price exactly as it is.
         */
        ...(draft.followsMarket
          ? { followsMarket: true, marketAdjust: draft.marketAdjust ?? null, marketBasis: draft.marketBasis || 'low' }
          : {}),
        /*
         * WHICH ARTWORK, sent separately from the printing above and never
         * derived from it. Absent means the seller was not asked — one artwork,
         * or none the publisher lists — and the listing shows the catalogue's
         * picture, as every listing did before the question existed.
         */
        photoIds,
      });
      /*
       * Into YOUR listings, not into browse.
       *
       * It used to push the new card onto `listings`, which is what browse reads —
       * and browse deliberately excludes your own cards now, so the optimistic
       * insert put it in the one place it must not appear while leaving the
       * Selling tab empty until a full page reload. Reported as "I have to
       * refresh after every listing", which is exactly what it was.
       */
      loadMine();
      track('listing.post');
      notify(t("Listed. Traders within {radius} see it now.", { radius }));
      go('you', { tab: 'selling' });
      return r.listing;
    } catch (e) {
      notify(e.message, 'caution');
      throw e;
    }
  };

  /**
   * Propose a trade against someone's listing.
   *
   * The meetup is optional here on purpose: agreeing to swap and agreeing where to
   * meet are two different conversations, and forcing both at once makes people
   * guess a place before they know the other side is even interested. The recipient
   * settles it when they accept.
   */
  const proposeTrade = async ({ listingId, items, quantity, cashCents, placeName, placeNote, meetAt }) => {
    if (!requireAuth('propose a trade')) return;
    try {
      const r = await api.proposeTrade({
        listingId, items,
        // Omitted when it is one, so an ordinary single-copy proposal sends the
        // same body it always did and the server's own default is what answers.
        ...(quantity && quantity > 1 ? { quantity } : {}),
        ...(cashCents ? { cashCents } : {}),
        ...(placeName ? { placeName } : {}),
        ...(placeNote ? { placeNote } : {}),
        ...(meetAt ? { meetAt } : {}),
      });
      const view = api.toTradeView(r.trade);
      setTrades((ts) => [view, ...ts]);
      track('trade.propose');
      notify(t("Trade proposed. We'll ping you when {name} replies.", { name: view.them.displayName.split(' ')[0] }));
      /*
       * A SHOP lands on what it just sent, in its own Requests (shop trades
       * view, ticket 08); a person on their Proposed trades, as before.
       */
      if (actingAsShop) goRequests({ tab: 'sent', id: view.id });
      else go('trades', { tab: 'proposed' });
      return view;
    } catch (e) {
      notify(e.message, 'caution');
      throw e;
    }
  };

  // English keys, translated where they are shown — the same rule as everywhere
  // else. A translated string built here would be frozen at the locale this
  // component first rendered in.
  const NOTE = {
    accept: 'Meetup confirmed. It is in your active list.',
    decline: 'Declined. Nobody was charged.',
    cancel: 'Trade cancelled. Nobody was charged.',
    counter: 'Sent back with your offer. It is their turn now.',
    complete: null,   // depends on whether the other side has confirmed yet
  };

  /** accept | decline | counter | cancel | complete */
  /*
   * `answers`: refusal codes the caller handles by acting rather than by a
   * sentence — a shop's Requests row answers `meetup_required` by asking for
   * the place and the time — so no toast is shown for them here.
   */
  const actOnTrade = async (id, action, body, { answers = [] } = {}) => {
    try {
      const r = await api.tradeAction(id, action, body);
      const view = api.toTradeView(r.trade);
      setTrades((ts) => ts.map((x) => (x.id === id ? view : x)));
      if (action === 'complete') {
        notify(view.status === 'completed'
          ? t("Trade done. Both of you confirmed.")
          // Saying "waiting for X" is the honest message: one confirmation is not
          // a completed trade, and pretending otherwise is how disputes start.
          : t("Marked done on your side. Waiting for {name}.", { name: view.them.displayName.split(' ')[0] }));
      } else {
        notify(t(NOTE[action]), action === 'accept' ? 'success' : 'info');
      }
      return view;
    } catch (e) {
      if (e && answers.includes(e.code)) {
        // The caller answers this one; the trade did not change, so nothing to reload.
        throw e;
      }
      /*
       * The one refusal worth a sentence of its own.
       *
       * A seller pressing Accept on a trade for three copies when one is left
       * needs the number, not "there are not that many": the number is what
       * tells them whether to cancel another trade, ask the buyer to take one,
       * or decline. The server sends both figures precisely so this can be
       * said, and the generic message is the fallback for a body that did not
       * arrive.
       */
      if (e && e.code === 'insufficient_stock' && e.payload) {
        notify(t("This asks for {asked} but only {left} are still free. Cancelling an accepted trade puts copies back.",
          { asked: e.payload.requested, left: e.payload.available }), 'caution');
      } else {
        notify(e.message, 'caution');
      }
      // Whatever went wrong, the server's view is now the one that counts.
      loadTrades();
      throw e;
    }
  };

  /**
   * Opens a conversation and loads its messages.
   *
   * Reading is what the API call does — there is no separate "mark read", because
   * that would be a second round trip that can fail on its own and leave a badge
   * nobody can clear.
   */
  const readThread = React.useCallback(async (id) => {
    if (!id) return;
    /*
     * Reading the conversation already on screen keeps what it shows until the
     * answer arrives. The one time that happens is the first message of a new
     * conversation: it is on screen from the send, and blanking it to a
     * skeleton for a round trip would look like the message had gone.
     */
    setOpenThread((prev) => (prev.id === id ? { ...prev, loading: true } : { id, messages: [], loading: true }));
    try {
      const r = await api.conversation(id);
      // `cards`: the listing links' cards, keyed by listing id, read by the
      // server with the messages — one request for the whole thread.
      setOpenThread({ id, messages: r.messages.map(api.toMessageView), cards: r.listings || {}, loading: false });
      // The badge clears here rather than after a reload: the server has already
      // marked it read, and waiting for a refetch to reflect that looks broken.
      setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, unread: 0 } : t)));
    } catch (e) {
      setOpenThread((prev) => (prev.id === id ? { ...prev, loading: false } : { id, messages: [], loading: false }));
      notify(e.message, 'caution');
    }
  }, [notify]);

  /**
   * Opens the conversation with a person, existing or not.
   *
   * Every caller asks the same thing — "let me talk to them" — and none of them
   * should have to know whether a thread exists yet. Previously each one searched
   * the thread list itself and silently did nothing useful when it found none.
   */
  const messageTrader = React.useCallback((traderId, displayName) => {
    const existing = threads.find((t) => t.with && t.with.id === traderId);
    if (existing) { setPending(null); navigate('inbox', { id: existing.id }); return; }
    // No conversation yet. The recipient is held in state rather than the URL: an
    // id in a URL would need a name fetched to display, and a name in a URL would
    // be untrusted text rendered as a person. A reload simply lands on the inbox
    // list, which is honest — nothing has been said yet.
    setPending({ id: traderId, displayName: displayName || 'Trader' });
    // `to` gives this screen an address of its own. The NAME still stays in
    // state — a name in a URL is untrusted text rendered as a person — and a
    // reload still lands on the list, which is what the note above promises.
    // What the id buys is a back button that has something to undo.
    navigate('inbox', { to: traderId });
  }, [threads, navigate]);

  /**
   * Sends a message, addressed to the PERSON rather than the conversation.
   *
   * That is what the API takes, and it is why the first message to somebody needs
   * no "start a conversation" step — there is nothing to create first, and no race
   * when both sides write at once.
   */
  const sendMessage = async (toTraderId, text, { noCard = false } = {}) => {
    const body = text.trim();
    if (!body) return;
    try {
      const r = await api.sendMessage(toTraderId, body, noCard);
      setOpenThread((prev) => (prev.id === r.conversationId || prev.id === null
        ? {
          ...prev, id: r.conversationId, messages: [...prev.messages, api.toMessageView(r.message)],
          cards: { ...(prev.cards || {}), ...(r.listings || {}) },
        }
        : prev));
      /*
       * THE CONVERSATION NOW EXISTS, SO IT GETS ITS ADDRESS.
       *
       * The first message to somebody is written at `/inbox?to=<their id>`, an
       * address naming a person, because there was no conversation to name.
       * Putting the person down here used to leave nothing saying which
       * conversation was open, and on a phone the inbox fell back to its list —
       * the sender was dropped out of the thread they had just started. So the
       * address becomes `?id=`, replacing the `?to=` entry rather than adding
       * one: no dead step that shows the empty conversation again (on a phone,
       * InboxScreen has already put the list behind it).
       *
       * The person is kept, with the new id, until the conversation list has
       * been read again: until then the list does not hold this conversation,
       * and the screen would have nothing to draw it from. Only if the sender is
       * still on that screen — the send is a round trip, and they may have left.
       * Read from `window.location`, not `route`: the closure's `route` is the
       * one from before the await.
       */
      const startedHere = window.location.pathname === '/inbox'
        && new URLSearchParams(window.location.search).get('to') === toTraderId;
      if (startedHere) {
        setPending((p) => (p && p.id === toTraderId ? { ...p, conversationId: r.conversationId } : p));
        navigate('inbox', { id: r.conversationId }, { replace: true });
      }
      // Put down only once the list holds it, and only if it is still this
      // person: a failed read leaves the screen on the stub rather than the
      // list, and somebody messaged in the meantime is not erased.
      const listed = await loadConversations();
      if (listed) setPending((p) => (p && p.id === toTraderId ? null : p));
      return r.conversationId;
    } catch (e) {
      notify(e.message, 'caution');
      throw e;
    }
  };

  const radiusKm = parseFloat(radius);
  const value = {
    route, go, navigate, back, canBack,
    trader, booting, locationsReady, signup, login, logout, requireAuth, intent, loadListings, listingsState,
    locale, setLocale, t, serverConfig,
    listings, myCards, myCardsState, myCardsTotal, myCardsByGame, loadMine, wants, wantsState, wantsTotal, addWant, removeWant, removeWants, publishListing, withdrawListing, withdrawListings, editListing, setFollowingInBulk, bulkPrice,
    /*
     * Browse's filters, resolved, and the counts that were resolved WITH them.
     *
     * Handed out rather than recomputed on the screen, because two places
     * deciding what is being shown is how a tab's count comes to describe a
     * different list than the grid under it. The screen renders these; the
     * request was built from the same values.
     */
    activeGames, activeLanguages, browseCounts, sort, setSort,
    cursors, loadMore, loadingMore,
    trades, tradesState, loadTrades, proposeTrade, actOnTrade,
    referral, referralState, loadReferral,
    campaigns, campaignsState, loadCampaigns, liveCampaign,
    unrevealed, revealId, checkResult, closeReveal, settleResult,
    inviteCode, setInviteCode,
    threads, threadsState, openThread, loadConversations, sendMessage, readThread, messageTrader, pending,
    /*
     * Closing a conversation that has no messages in it yet.
     *
     * `pending` is a person, not a thread — `messageTrader` holds the recipient
     * in state deliberately, because an id in the URL would need a name fetched
     * and a name in the URL would be untrusted text rendered as a person. The
     * cost of that is a conversation with no address of its own: it renders at
     * `/inbox`, the list's own URL. So nothing about navigating can close it, and
     * the back button needs a way to say "not that person any more".
     */
    clearPending,
    radius, setRadius: setRadiusPersisting, radiusKm, point, setPoint, games, setGames: setGamesPersisting,
    /*
     * The saved places, and which one the product is currently measuring from.
     *
     * `activeLocation` is derived rather than stored: the server marks exactly one
     * row active, and a second copy of that flag in the client is a second thing
     * that can be wrong.
     */
    locations, activeLocation: locations.find((l) => l.active) || null,
    addLocation, editLocation, activateLocation, removeLocation,
    languages, setLanguages: setLanguagesPersisting,
    settings, setSettings, profile, setNotifyPreference, saveContact,
    actAs, myShop,
    toast, notify, finishOnboarding, replayOnboarding, clearToast: () => setToast(null),
    /*
     * THE HEADER'S TWO COUNTS ARE THE SAME NUMBERS AS THE TITLE'S AND THE
     * CHIME'S: the list on its own screen, the pulse everywhere else (see
     * `unreadMessages` / `waitingTrades`). They were built from the lists
     * alone, which are only fetched on their own screens, so off them the
     * badge never moved while the chime rang — reported on production,
     * 2026-10-16, and pinned by badge-live.spec.
     */
    unread: unreadMessages,
    /*
     * THE BACKGROUND IMPORT, WHEREVER YOU ARE.
     *
     * Asked for: "it would be nice if the user had some kind of indication of
     * state and/or progress of the background task somewhere." The import
     * screen knew; nowhere else did — which is backwards, because the whole
     * offer of the slow path is that you can go and be somewhere else.
     *
     * It rides on the pulse rather than polling for itself. A second timer
     * would be a request every few seconds, from every tab in the product, to
     * tell almost all of them that nothing is importing.
     *
     * Null for a guest, for anybody with no import, and for one whose result
     * they have already been shown.
     */
    runningImport: (pulse && pulse.import) || null,
    /**
     * What is waiting on YOU, not merely what is unfinished.
     *
     * Named `pendingTrades`, not `pending`. It was `pending`, which this object
     * already used for the trader you are about to message for the first time —
     * a duplicate key, so the number silently won and the inbox built a
     * conversation whose `with` was an integer. Messaging somebody new was broken
     * for anybody who had a trade awaiting a reply, which is most people who use
     * the product at all.
     */
    pendingTrades: waitingTrades,
    /*
     * The same two counts off the LISTS alone, for the shop's back office: its
     * trades list is loaded under the scope, and its rail badge counts only the
     * games in scope (the owner's Q12, 2026-10-13) — which the pulse cannot.
     */
    listedUnread: threads.reduce((n, x) => n + (x.unread || 0), 0),
    listedPendingTrades: trades.filter(waitsOnYou).length,
    /** Working as a shop: its trades live in the back office's Requests. */
    actingAsShop,
    goRequests,
    pastTotal,

    /** Trades with a meetup both sides have agreed. */
    meetups: trades.filter((x) => x.status === 'accepted'),
  };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

